// Score Studio: click the staff to place notes, select to edit/delete, with
// undo/redo, MusicXML import/export, and a sample lead sheet. The store's
// score IS the truth (SPEC §Migration slice 4) — the Score edits the SAME
// tune the Workbench plays, and this page renders the truth directly
// (scoreToSong), no lift needed.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { ScoreView, type StaffClick } from '@/components/ScoreView';
import { ScoreNoteEditor } from '@/components/ScoreNoteEditor';
import { ScoreNoteheadControls } from '@/components/ScoreNoteheadControls';
import { ScoreExpressionControls } from '@/components/ScoreExpressionControls';
import { ScoreChordEditor } from '@/components/ScoreChordEditor';
import { Play } from 'lucide-react';
import { ChartHeader } from '@/components/chartChrome';
import { Popover } from '@/components/Popover';
import { PianoInput } from '@/components/PianoInput';
import { ScoreFormEditor } from '@/components/ScoreFormEditor';
import { ScoreFormOverview } from '@/components/ScoreFormOverview';
import { useStore } from '@/state/store';
import { allAlternativeShapes } from '@/music/alternativeShapes';
import { scaleVars } from '@/state/displayScale';
import { ScaleSlider } from '@/components/ScaleSlider';
import { useNotationMetrics } from '@/components/useNotationMetrics';
import { useScorePages } from '@/components/useScorePages';
import { SheetPageCards } from '@/components/PagedSheet';
import { PrintSheet } from '@/components/PrintSheet';
import { usePrintMode } from '@/components/printMode';
import { scoreHasLyrics, scoreToStaffSong, writtenKeySignature } from '@/music/score/toSong';
import {
  DURATION_SHORTCUTS,
  entryDurationInBeats,
  entryGridInBeats,
  entryKeySignature,
  entryMidiForLetter,
  entrySpellingForLetter,
  toggleDottedDuration,
  type EntryAccidental,
} from '@/music/score/entry';
import { invertInterval, noteNameOf, spellMidi, transposePitch } from '@/music/score/pitch';
import {
  describeEvent,
  findEvent,
  getExpressionMarks,
  getNoteHeads,
  defaultNoteHeadIndex,
} from '@/music/score/voiceEdit';
import { findChord } from '@/music/score/formEdit';
import { chartChordSymbol } from '@/music/chart';
import type { PartPreset } from '@/music/score/partEdit';
import { getPartStaffSplitCapability } from '@/music/score/partSplit';
import { songWithoutLyrics, songForLowerStaff } from '@/music/song';
import { BUNDLED_SAMPLES, SCORE_EXAMPLES } from '@/data/scoreExamples';
import { BUNDLED_SCORES, loadBundledScore } from '@/data/bundledScores';
import {
  loadOpenbookScore,
  openbookLibrary,
  type OpenbookLibraryEntry,
} from '@/data/openbookLibrary';
import {
  entriesFromFileList,
  listSamplesFolder,
  loadFolderHandle,
  pickSamplesFolder,
  readScoreFile,
  saveFolderHandle,
  supportsFolderPicker,
  verifyFolderPermission,
  type SampleEntry,
} from '@/components/samplesFolder';
import { snapBeat } from '@/music/notation/editing';
import { barGridBeat, buildTimeline, type TimelineSound } from '@/music/notation/timeline';
import { buildScoreExpression } from '@/music/score/expression';
import { scoreForPlaybackVisit } from '@/music/score/playbackRules';
import type { ClipboardPasteMode } from '@/music/score/clipboard';
import { barAuditionRange, normalizeAuditionRange } from '@/music/notation/scoreAudition';
import { adjacentScoreEvents } from '@/music/notation/scoreNavigation';
import type { Part, Score } from '@/music/score/types';
import { useScorePlayer } from '@/audio/useScorePlayer';
import { useBandArrangement } from '@/audio/useBandArrangement';
import { BandTransport } from '@/components/BandTransport';
import { bandPlaybackStatus } from '@/components/bandPlaybackStatus';
import { usePassageSelection } from '@/components/passageSelection';
import { engraverClef, useAnalysisLane, useBandPlayheadBeat } from '@/components/scoreShared';
import type { Song } from '@/music/song';
import type { Clef as EngraverClef } from '@/music/notation/pitch';
import { TuneWorkspaceHeader } from '@/components/TuneWorkspaceHeader';
import { Toast } from '@/components/Toast';
import { isAbcSource } from '@/music/abc/book';
import { getChordPlayer } from '@/audio/player';
import { midiOf } from '@/music/score/pitch';
import { durationQuarters, fractionToNumber } from '@/music/score/duration';
import './ScorePage.css';

function previewSound(part: Part | undefined): TimelineSound {
  if (part?.instrument === 'piano') return 'piano';
  if (part?.instrument === 'bass' || part?.role === 'bass') return 'bass';
  if (part?.instrument === 'guitar' || part?.role === 'comp') return 'guitar';
  if (part?.instrument === 'other' && part.role !== 'lead') return 'guitar';
  return 'lead';
}

let uid = 0;
const m = (midi: number, beat: number, duration: number) => ({
  id: `s${uid++}`,
  midi,
  beat,
  duration,
});
const SAMPLE: Song = {
  id: 'sample',
  title: 'ii–V–I sample',
  key: 'C',
  timeSignature: [4, 4],
  sections: [
    {
      id: 'main',
      name: 'Main',
      bars: [
        {
          id: 'b1',
          beats: 4,
          chords: [{ beat: 1, duration: 4, root: 'D', chordType: 'min7' }],
          melody: [m(69, 1, 0.5), m(71, 1.5, 0.5), m(72, 2, 1), m(74, 3, 2)],
        },
        {
          id: 'b2',
          beats: 4,
          chords: [{ beat: 1, duration: 4, root: 'G', chordType: 'dom7' }],
          melody: [m(71, 1, 1), m(74, 2, 1), m(77, 3, 2)],
        },
        {
          id: 'b3',
          beats: 4,
          chords: [{ beat: 1, duration: 4, root: 'C', chordType: 'maj7' }],
          melody: [m(76, 1, 4)],
        },
        {
          id: 'b4',
          beats: 4,
          chords: [{ beat: 1, duration: 4, root: 'C', chordType: 'maj7' }],
          melody: [m(72, 1, 4)],
        },
      ],
    },
  ],
};

const DURATIONS: { label: string; mobileLabel: string; beats: number; shortcut?: string }[] = [
  { label: 'Whole', mobileLabel: 'Whole', beats: 4, shortcut: '7' },
  { label: 'Half', mobileLabel: 'Half', beats: 2, shortcut: '6' },
  { label: 'Dotted quarter', mobileLabel: 'Dot', beats: 1.5 },
  { label: 'Quarter', mobileLabel: 'Qtr', beats: 1, shortcut: '5' },
  { label: 'Eighth', mobileLabel: '8th', beats: 0.5, shortcut: '4' },
  { label: 'Sixteenth', mobileLabel: '16th', beats: 0.25, shortcut: '3' },
];

const ENTRY_ACCIDENTALS: {
  value: EntryAccidental;
  label: string;
  shortcut: string;
  title: string;
}[] = [
  { value: 'key', label: 'Key', shortcut: 'K', title: 'Follow the key signature' },
  { value: 'natural', label: '♮', shortcut: '=', title: 'Natural on the next note' },
  { value: 'sharp', label: '♯', shortcut: '#', title: 'Sharp on the next note' },
  { value: 'flat', label: '♭', shortcut: '-', title: 'Flat on the next note' },
];

const PART_PRESET_OPTIONS: {
  value: PartPreset;
  label: string;
  description: string;
}[] = [
  { value: 'lead', label: 'Lead', description: 'Melody or horn line' },
  { value: 'guitar', label: 'Guitar', description: 'Written comping or chord melody' },
  { value: 'bass', label: 'Bass', description: 'Written bars with automatic fallback' },
  { value: 'piano', label: 'Piano', description: 'Editable right- and left-hand grand staff' },
];

function entryDurationLabel(beats: number): string {
  const toolbar = DURATIONS.find((duration) => Math.abs(duration.beats - beats) < 1e-9);
  if (toolbar) return toolbar.label.toLowerCase();
  const named: Record<string, string> = {
    '0.25': 'sixteenth',
    '0.75': 'dotted eighth',
    '3': 'dotted half',
    '6': 'dotted whole',
  };
  return named[String(beats)] ?? `${Number(beats.toFixed(3))}-beat`;
}

function isDottedEntryDuration(beats: number): boolean {
  return [0.375, 0.75, 1.5, 3, 6].some((duration) => Math.abs(duration - beats) < 1e-9);
}

export function ScorePage() {
  const score = useStore((s) => s.score);
  const chart = useStore((s) => s.chart);
  const store = useStore;
  // Part picker: a multi-part score is edited one part/staff at a time. The
  // store keeps that authored target so mouse, keyboard and piano entry all
  // write to the same instrument and staff.
  const [partIndex, setPartIndex] = useState(() =>
    Math.max(
      0,
      score.parts.findIndex((part) => part.id === store.getState().scoreContentTarget?.partId),
    ),
  );
  const [playbackScope, setPlaybackScope] = useState<'band' | 'score' | 'part'>('band');
  const [auditionSelection, setAuditionSelection] = useState<string | null>(null);
  const [renamingPart, setRenamingPart] = useState(false);
  const [partName, setPartName] = useState('');
  const [partPendingRemovalId, setPartPendingRemovalId] = useState<string | null>(null);
  const scoreContentTarget = useStore((s) => s.scoreContentTarget);
  const previousTarget = useRef(scoreContentTarget);
  // partIndex -1 = the stacked FULL SCORE: every part's every staff, jointly
  // engraved on one system grid (note editing needs a single part).
  const fullScore = partIndex === -1 && score.parts.length > 1;
  // Lead-sheet content (one part over the harmony lane) engraves on the
  // book's grid: four bars a line at the book's density.
  const leadSheet = !fullScore && score.form.some((fm) => fm.harmony.length > 0);
  // Shared with the Workbench's Score view: one persisted S/M/L choice.
  const scoreScale = useStore((s) => s.scoreScale);
  const setScoreScale = useStore((s) => s.setScoreScale);
  const targetPartIndex = score.parts.findIndex((part) => part.id === scoreContentTarget?.partId);
  const activePart = fullScore
    ? 0
    : targetPartIndex >= 0
      ? targetPartIndex
      : Math.min(Math.max(0, partIndex), Math.max(0, score.parts.length - 1));
  const activePartDefinition = score.parts[activePart];
  const activeStaffIndex =
    !fullScore &&
    activePartDefinition &&
    scoreContentTarget?.partId === activePartDefinition.id &&
    scoreContentTarget.staffIndex >= 0 &&
    scoreContentTarget.staffIndex < activePartDefinition.staves.length
      ? scoreContentTarget.staffIndex
      : 0;
  const activeStaffDefinition = activePartDefinition?.staves[activeStaffIndex];
  const hasMultipleStaves = (activePartDefinition?.staves.length ?? 0) > 1;
  const staffSplitCapability = useMemo(
    () => getPartStaffSplitCapability(activePartDefinition),
    [activePartDefinition],
  );
  const pianoHandControls =
    hasMultipleStaves &&
    activePartDefinition?.staves.length === 2 &&
    (activePartDefinition.instrument === 'piano' ||
      /(^|\W)(piano|pno)(\W|$)/i.test(activePartDefinition.name));
  const editable = !fullScore;
  const canEditSecondVoice = !hasMultipleStaves;
  useEffect(() => {
    // History snapshots clone the target. Only an actual part/staff change
    // should leave Full score when undoing a harmony correction.
    const targetChanged =
      previousTarget.current?.partId !== scoreContentTarget?.partId ||
      previousTarget.current?.staffIndex !== scoreContentTarget?.staffIndex;
    previousTarget.current = scoreContentTarget;
    // Undo can restore a different instrument/staff. Follow that authored
    // target before the visible picker has a chance to overwrite it.
    if (targetChanged && scoreContentTarget) {
      const restoredIndex = score.parts.findIndex((part) => part.id === scoreContentTarget.partId);
      if (restoredIndex >= 0 && restoredIndex !== partIndex) {
        setPartIndex(restoredIndex);
        return;
      }
    }
    // A score load resets the session target. Keep the visible part
    // authoritative so a same-length Lead+Bass replacement cannot leave the
    // Bass staff on screen while keyboard or mouse entry silently writes Lead.
    if (fullScore) return;
    if (score.parts.length === 0) {
      if (scoreContentTarget) store.setState({ scoreContentTarget: null });
      return;
    }
    const nextIndex = partIndex >= 0 && partIndex < score.parts.length ? partIndex : 0;
    if (nextIndex !== partIndex) setPartIndex(nextIndex);
    const part = score.parts[nextIndex];
    const validStaff =
      scoreContentTarget?.partId === part.id &&
      scoreContentTarget.staffIndex >= 0 &&
      scoreContentTarget.staffIndex < part.staves.length;
    if (!validStaff) {
      // Reconciliation is not a user selection change: preserve a freshly
      // advanced caret when the first on-demand Lead part has just appeared.
      store.setState({ scoreContentTarget: { partId: part.id, staffIndex: 0 } });
    }
  }, [fullScore, partIndex, score.parts, scoreContentTarget, store]);
  const showLyrics = useStore((s) => s.showLyrics);
  const showBarNumbers = useStore((s) => s.showBarNumbers);
  const hasLyrics = useMemo(() => scoreHasLyrics(score), [score]);
  const showAnalysis = useStore((s) => s.showAnalysis);
  const song = useMemo(() => {
    const raw = scoreToStaffSong(score, 0, activePart);
    return showLyrics ? raw : songWithoutLyrics(raw);
  }, [score, activePart, showLyrics]);
  const analysisLane = useAnalysisLane(song, showAnalysis);
  const activeExtraStaves = useMemo(() => {
    if (fullScore || !activePartDefinition) return [];
    return activePartDefinition.staves.slice(1).map((staffDef, offset) => {
      const staffIndex = offset + 1;
      const projected = scoreToStaffSong(score, staffIndex, activePart);
      return {
        song: showLyrics ? projected : songWithoutLyrics(projected),
        clef: engraverClef(staffDef.clef, activePartDefinition.id, activePartDefinition.instrument),
      };
    });
  }, [activePart, activePartDefinition, fullScore, score, showLyrics]);
  const allStaves = useMemo(() => {
    const entries: {
      song: Song;
      clef: EngraverClef;
      semitones: number;
      sound: TimelineSound;
      meta: { label?: string; group?: string };
    }[] = [];
    score.parts.forEach((part, pi) => {
      part.staves.forEach((staffDef, si) => {
        const projected = showLyrics
          ? scoreToStaffSong(score, si, pi)
          : songWithoutLyrics(scoreToStaffSong(score, si, pi));
        entries.push({
          song: entries.length === 0 ? projected : songForLowerStaff(projected),
          clef: engraverClef(staffDef.clef, part.id, part.instrument),
          semitones: -(part.transposition?.semitones ?? 0),
          sound: previewSound(part),
          // Label the part's first staff; the group braces multi-staff parts
          // and drives the ensemble bracket.
          meta: {
            ...(si === 0 ? { label: part.name || `Part ${pi + 1}` } : {}),
            group: part.id,
          },
        });
      });
    });
    return entries;
  }, [score, showLyrics]);
  const fullStaves = fullScore ? allStaves : null;
  const selectedId = useStore((s) => s.selectedNoteId);
  const selectedHeadIndex = useStore((s) => s.selectedNoteheadIndex);
  const selectedHeads = selectedId ? getNoteHeads(score, selectedId) : null;
  const selectedHead =
    selectedHeads?.[
      selectedHeadIndex ?? (selectedId ? defaultNoteHeadIndex(score, selectedId) : 0)
    ];
  const selectedEvent = selectedId ? describeEvent(score, selectedId) : null;
  const selectedSlot = selectedId ? findEvent(score, selectedId)?.slot : null;
  const selectedExpression = selectedId ? getExpressionMarks(score, selectedId) : null;
  const selectedPitch = selectedHead?.pitch;
  const selectedWrittenPitch = (() => {
    if (!selectedPitch || !selectedEvent) return null;
    const transposition = score.parts[selectedEvent.partIndex].transposition;
    if (!transposition) return selectedPitch;
    try {
      return transposePitch(selectedPitch, transposition);
    } catch {
      // Match the staff renderer when the written spelling would need a
      // triple accidental, which the native pitch model cannot represent.
      return spellMidi(midiOf(selectedPitch) + transposition.semitones);
    }
  })();
  const selectedPitchLabel = selectedEvent?.rest
    ? 'Rest'
    : selectedWrittenPitch
      ? `${noteNameOf(selectedWrittenPitch)}${selectedWrittenPitch.octave}`
      : selectedEvent?.midi != null
        ? `Note ${selectedEvent.midi}`
        : 'Note';
  const noteheadControls =
    selectedId && selectedHeads?.length && selectedEvent && selectedWrittenPitch ? (
      <ScoreNoteheadControls
        key={selectedId}
        heads={selectedHeads.map((head) => {
          const transposition = score.parts[selectedEvent.partIndex].transposition;
          if (!transposition) return head.pitch;
          try {
            return transposePitch(head.pitch, transposition);
          } catch {
            return spellMidi(midiOf(head.pitch) + transposition.semitones);
          }
        })}
        selectedIndex={selectedHeadIndex ?? defaultNoteHeadIndex(score, selectedId)}
        onSelect={(index) => store.getState().selectNote(selectedId, index)}
        onAdd={(writtenPitch) => {
          const transposition = score.parts[selectedEvent.partIndex].transposition;
          let concertPitch = writtenPitch;
          if (transposition) {
            try {
              concertPitch = transposePitch(writtenPitch, invertInterval(transposition));
            } catch {
              concertPitch = spellMidi(midiOf(writtenPitch) - transposition.semitones);
            }
          }
          store.getState().scoreAddChordHead(concertPitch);
        }}
        onRemove={() => store.getState().scoreRemoveChordHead()}
      />
    ) : null;
  const selectedMeasureId = useStore((s) => s.selectedMeasureId);
  const selectedChordId = useStore((s) => s.selectedChordId);
  const selectedChord = selectedChordId ? findChord(score, selectedChordId) : null;
  const reharmPreview = useStore((s) => s.reharmPreview);
  const playheadTint = useStore((s) => s.playheadTint);
  const hasClipboard = useStore(
    (s) => (s.noteClipboard?.notes.length ?? 0) > 0 || (s.noteClipboard?.bars?.length ?? 0) > 0,
  );
  const hasStructuralClipboard = useStore((s) => (s.noteClipboard?.bars?.length ?? 0) > 0);
  const { passageIdSet, onSelectPassage, onClearPassage } = usePassageSelection();
  const entryCursor = useStore((s) => s.scoreEntryCursor);
  const entryDuration = useStore((s) => s.entryDuration);
  const entryVoice = useStore((s) => s.entryVoice);
  const canUndo = useStore((s) => s.melodyPast.length > 0);
  const canRedo = useStore((s) => s.melodyFuture.length > 0);
  const fileRef = useRef<HTMLInputElement>(null);
  const dirRef = useRef<HTMLInputElement>(null);
  const scoreSurfaceRef = useRef<HTMLDivElement | null>(null);
  const editorDockRef = useRef<HTMLDivElement | null>(null);
  const pageRef = useRef<HTMLElement | null>(null);
  const [compact, setCompact] = useState(
    () => window.matchMedia?.('(max-width: 700px)').matches ?? false,
  );
  const [clipboardNotice, setClipboardNotice] = useState('');
  const [visibleMeasureIds, setVisibleMeasureIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  useEffect(() => {
    const query = window.matchMedia?.('(max-width: 700px)');
    if (!query) return;
    const update = () => setCompact(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  useEffect(() => {
    const dock = editorDockRef.current;
    const guide = dock?.querySelector<HTMLElement>('.score-entry-guide');
    if (!dock || !guide || typeof ResizeObserver === 'undefined') return;
    const update = () => dock.style.setProperty('--score-guide-offset', `${guide.offsetTop}px`);
    const observer = new ResizeObserver(update);
    observer.observe(dock);
    update();
    return () => observer.disconnect();
  }, []);
  const [pasteText, setPasteText] = useState('');
  const [showPaste, setShowPaste] = useState(false);
  // "Book name · 24 tunes": a tunebook opened here loads its first tune; the
  // rest are the Tunes browser's loaded list.
  const [importNotice, setImportNotice] = useState<string | null>(null);
  useEffect(() => {
    if (!importNotice) return;
    const timer = window.setTimeout(() => setImportNotice(null), 3200);
    return () => window.clearTimeout(timer);
  }, [importNotice]);
  // Local samples folder: its files join the Examples dropdown. A stored
  // handle reconnects silently when its grant survived; otherwise the button
  // offers a one-click re-grant (needs the user gesture).
  const [samples, setSamples] = useState<SampleEntry[]>([]);
  const [samplesFolder, setSamplesFolder] = useState<string | null>(null);
  const [samplesPending, setSamplesPending] = useState(false);
  // The full openbook corpus, listed/loaded through the shared library
  // module (the same source the palette and tune URLs use).
  const [openbookLib, setOpenbookLib] = useState<OpenbookLibraryEntry[]>([]);
  useEffect(() => {
    let cancelled = false;
    void openbookLibrary().then((entries) => {
      if (!cancelled) setOpenbookLib(entries);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const openbookOptions = useMemo(
    () => openbookLib.map((e) => ({ value: `openbook:${e.id}`, label: e.title })),
    [openbookLib],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const handle = await loadFolderHandle();
      if (!handle || cancelled) return;
      if (await verifyFolderPermission(handle, false)) {
        const entries = await listSamplesFolder(handle);
        if (!cancelled) {
          setSamples(entries);
          setSamplesFolder(handle.name);
        }
      } else if (!cancelled) {
        setSamplesFolder(handle.name);
        setSamplesPending(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const connectSamplesFolder = async () => {
    // A remembered folder whose grant lapsed: one click re-grants and relists.
    if (samplesPending) {
      const stored = await loadFolderHandle();
      if (stored && (await verifyFolderPermission(stored, true))) {
        setSamples(await listSamplesFolder(stored));
        setSamplesFolder(stored.name);
        setSamplesPending(false);
        return;
      }
      setSamplesPending(false); // fall through to picking afresh
    }
    if (!supportsFolderPicker()) {
      dirRef.current?.click();
      return;
    }
    try {
      const dir = await pickSamplesFolder();
      const entries = await listSamplesFolder(dir);
      setSamples(entries);
      setSamplesFolder(dir.name);
      setSamplesPending(false);
      await saveFolderHandle(dir);
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') return; // user cancelled
      window.alert(
        `Folder failed to open: ${err instanceof Error ? err.message : 'unknown error'}`,
      );
    }
  };

  const onSamplesDir = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const entries = entriesFromFileList(files);
    setSamples(entries);
    setSamplesFolder(entries.length > 0 ? 'Session folder' : null);
    setSamplesPending(false);
    e.target.value = '';
  };
  const [restMode, setRestMode] = useState(false);
  const [writeMode, setWriteMode] = useState(false);
  const [arrangeMode, setArrangeMode] = useState(false);
  const [deletedScore, setDeletedScore] = useState<Score | null>(null);
  const [entryAccidental, setEntryAccidental] = useState<EntryAccidental>('key');
  const selectedQuarterDuration = selectedSlot?.source
    ? fractionToNumber(durationQuarters(selectedSlot.source.duration))
    : selectedEvent
      ? selectedEvent.duration /
        entryDurationInBeats(score, score.form[selectedEvent.measureIndex].id, 1)
      : entryDuration;
  const displayDuration = !writeMode && selectedEvent ? selectedQuarterDuration : entryDuration;
  const selectedFixedDuration =
    !writeMode && !!selectedEvent && (!!selectedSlot?.tupletGroup || selectedEvent.duration === 0);
  const chordEditorVisible = !writeMode && !arrangeMode && !!selectedChord;
  const compactNoteEditor =
    compact && editable && !writeMode && !arrangeMode && !!selectedEvent && !selectedChord;
  const compactDeleteUndo =
    compact &&
    editable &&
    !writeMode &&
    !arrangeMode &&
    !selectedEvent &&
    !selectedChord &&
    deletedScore === score;
  const compactEditorVisible = compactNoteEditor || compactDeleteUndo;
  const contextEditorVisible = compactEditorVisible || chordEditorVisible;
  useEffect(() => {
    if (selectedChordId && !findChord(score, selectedChordId)) store.getState().selectChord(null);
  }, [score, selectedChordId, store]);
  const adjacentNotes = useMemo(() => adjacentScoreEvents(score, selectedId), [score, selectedId]);
  useEffect(() => {
    const page = pageRef.current;
    const transport = page?.querySelector<HTMLElement>('.transport-bar');
    if (!page || !transport) return;
    const editor = page.querySelector<HTMLElement>(
      '.score-mobile-note-editor, .score-chord-editor',
    );
    const update = () => {
      page.style.setProperty(
        '--score-transport-clearance',
        `${Math.max(0, window.innerHeight - transport.getBoundingClientRect().top)}px`,
      );
      page.style.setProperty(
        '--score-note-editor-height',
        `${editor?.getBoundingClientRect().height ?? 0}px`,
      );
    };
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(transport);
    if (editor) observer?.observe(editor);
    window.addEventListener('resize', update);
    update();
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [compact, contextEditorVisible, compactDeleteUndo, selectedChordId, reharmPreview]);

  const [showPiano, setShowPiano] = useState(false);
  // Sized the same way every other engraved surface is (see
  // useNotationMetrics): px per staff space comes from the user's score
  // scale, and the system width follows the canvas. The Studio used to do
  // the reverse — fix a system width in staff spaces and let the SVG shrink
  // — which is why S/M/L meant something different here than on the
  // workbench despite writing the same setting.
  // Page view (the app-wide Pages setting): the engraved strip dealt into
  // side-by-side fitted pages, through the SAME pager the workbench score
  // cell uses. Editing rides along — pointer mapping is window-aware and the
  // caret draws inside its own system's group, so it appears on exactly the
  // page that holds it.
  const sheetPages = useStore((s) => s.sheetPages);
  const setSheetPages = useStore((s) => s.setSheetPages);
  const printing = usePrintMode();
  // Paper hides the editor's own sheet, so the pager stops measuring it.
  const pager = useScorePages(sheetPages && !printing);
  const {
    surfaceRef: metricsRef,
    spaceSize,
    systemWidth,
  } = useNotationMetrics(
    scoreScale,
    song.key,
    `${activePart}:${activeStaffIndex}:${fullScore}:${pager.paged ? 'pages' : 'flow'}`,
  );
  // Flowing (Pages off, or the pager declined to page): one div is both the
  // measured element and the keyboard-focus target. Paged: the OUTER
  // container keeps focus/keyboard (so typing works whichever page you
  // clicked, and the caret's scrollIntoView can find it anywhere in the
  // spread) while the measurement rides page 1's own surface, so systemWidth
  // follows the PAGE the music spans. Decided by what is RENDERED, not by the
  // preference: with Pages on a phone flows, and keying this off the setting
  // left nothing measured (the strip engraved at the metrics fallback). A
  // change of `pager.paged` re-attaches this ref, which is what hands the
  // measurement back when the pages go.
  const setSurfaceRef = useCallback(
    (node: HTMLDivElement | null) => {
      scoreSurfaceRef.current = node;
      // Rendered either way, so the pager can still see the window widen
      // while the strip flows.
      pager.probeRef.current = node;
      if (!pager.paged) metricsRef.current = node;
    },
    [metricsRef, pager.paged, pager.probeRef],
  );
  // The viewed part's own clef (a viola part engraves on the alto staff).
  const staffClef = useMemo(
    () =>
      engraverClef(
        score.parts[activePart]?.staves[0]?.clef,
        score.parts[activePart]?.id,
        score.parts[activePart]?.instrument,
      ),
    [score, activePart],
  );
  const chordSymbolStyle = useStore((s) => s.chordSymbolStyle);
  const engraveOptions = useMemo(
    () => ({
      spaceSize,
      systemWidth,
      clef: staffClef,
      chordSymbolStyle,
      ...(leadSheet && !fullScore ? { maxBarsPerSystem: 4 } : {}),
    }),
    [spaceSize, systemWidth, staffClef, leadSheet, fullScore, chordSymbolStyle],
  );

  // Every staff of the viewed part engraves on one shared onset grid. This is
  // a Piano grand staff for the app's preset, but imported parts can carry any
  // staff count and clef combination (for example Telemann's Ensemble).
  // A transposing part's Song is WRITTEN pitch; sound it at concert. The
  // full score sounds every staff, each with its own compensation.
  const partTransposition = score.parts[activePart]?.transposition;
  // Written audition reads the first visit; the band follows the expanded
  // form. Keep this sounding projection separate from the engraved score.
  const playbackScore = useMemo(() => scoreForPlaybackVisit(score), [score]);
  const playbackStaves = useMemo(() => playbackScore.parts.flatMap((part, pi) =>
    part.staves.map((_, si) => ({
      song: scoreToStaffSong(playbackScore, si, pi),
      semitones: -(part.transposition?.semitones ?? 0),
      sound: previewSound(part),
    })),
  ), [playbackScore]);
  const expressionByEvent = useMemo(() => buildScoreExpression(score), [score]);
  const beatsPerQuarterByBar = useMemo(() => {
    let denominator = 4;
    return score.form.map((measure) => {
      denominator = measure.timeSig?.[1] ?? denominator;
      return denominator / 4;
    });
  }, [score.form]);
  const fullTimeline = useMemo(() => {
    if (playbackStaves.length > 0) {
      return buildTimeline(
        playbackStaves[0].song,
        [],
        playbackStaves.slice(1).map((e) => e.song),
        {
          melodySemitones: playbackStaves[0].semitones,
          extraSemitones: playbackStaves.slice(1).map((e) => e.semitones),
          melodySound: playbackStaves[0].sound,
          extraSounds: playbackStaves.slice(1).map((e) => e.sound),
          expressionByEvent,
          beatsPerQuarterByBar,
        },
      );
    }
    return null;
  }, [playbackStaves, expressionByEvent, beatsPerQuarterByBar]);
  const partTimeline = useMemo(() => {
    return buildTimeline(
      scoreToStaffSong(playbackScore, 0, activePart),
      [],
      (activePartDefinition?.staves.slice(1) ?? []).map((_, index) =>
        scoreToStaffSong(playbackScore, index + 1, activePart),
      ),
      {
        melodySemitones: -(partTransposition?.semitones ?? 0),
        melodySound: previewSound(activePartDefinition),
        expressionByEvent,
        beatsPerQuarterByBar,
      },
    );
  }, [
    playbackScore,
    activePart,
    partTransposition,
    activePartDefinition,
    expressionByEvent,
    beatsPerQuarterByBar,
  ]);
  const timeline =
    (playbackScope === 'score' || fullScore) && fullTimeline ? fullTimeline : partTimeline;

  const tempo = useStore((s) => s.tempo);
  const speedPct = useStore((s) => s.speedPct);
  const previewTempo = Math.max(20, Math.round((tempo * speedPct) / 100));
  const player = useScorePlayer(timeline, previewTempo);
  // The Studio has no Cycle form control, so a cycle setting left on in the
  // workbench's comping lens must not silently cycle the band here.
  const savedAlternativeShapes = useStore((state) => state.alternativeShapes);
  const preferredAlternativeIds = useStore((state) => state.preferredAlternativeIds);
  const alternativeShapes = useMemo(
    () => allAlternativeShapes(savedAlternativeShapes),
    [savedAlternativeShapes],
  );
  const band = useBandArrangement({
    formCycleEnabled: false,
    alternativeShapes,
    preferredAlternativeIds,
  });
  const choosePlaybackScope = (scope: 'band' | 'score' | 'part') => {
    player.stop();
    getChordPlayer().stop();
    setAuditionSelection(null);
    setPlaybackScope(scope);
  };

  const bars = useMemo(() => song.sections.flatMap((s) => s.bars), [song]);
  const currentBarIndex = Math.max(
    0,
    writeMode && entryCursor
      ? score.form.findIndex((measure) => measure.id === entryCursor.measureId)
      : (selectedEvent?.measureIndex ??
          selectedChord?.measureIndex ??
          score.form.findIndex((measure) => measure.id === selectedMeasureId)),
  );
  const passageIndices = score.form.flatMap((measure, index) =>
    passageIdSet.has(measure.id) ? [index] : [],
  );
  const firstAuditionBar = passageIndices[0] ?? currentBarIndex;
  const lastAuditionBar = passageIndices.at(-1) ?? currentBarIndex;
  const auditionRange = barAuditionRange(timeline, firstAuditionBar, lastAuditionBar);
  const selectedChordBeat = selectedChord
    ? 1 +
      score.form[selectedChord.measureIndex].harmony
        .slice(
          0,
          score.form[selectedChord.measureIndex].harmony.findIndex(
            (chord) => chord.id === selectedChord.chord.id,
          ),
        )
        .reduce(
          (beat, chord) =>
            beat + (Number.isFinite(chord.beats) && chord.beats > 0 ? chord.beats : 1),
          0,
        )
    : 1;
  const caretBeat =
    passageIndices.length > 0
      ? 1
      : writeMode && entryCursor
        ? entryCursor.beat
        : (selectedEvent?.beat ?? selectedChordBeat);
  const caretPlaybackLastBar = passageIndices.length > 0 ? lastAuditionBar : score.form.length - 1;
  const caretBarRange = barAuditionRange(timeline, firstAuditionBar, caretPlaybackLastBar);
  const caretPlaybackRange =
    caretBarRange &&
    normalizeAuditionRange(timeline.totalBeats, {
      fromBeat:
        caretBarRange.fromBeat + (caretBeat - 1) * (timeline.barScales?.[firstAuditionBar] ?? 1),
      toBeat: caretBarRange.toBeat,
    });
  const auditionSource = playbackScope === 'score' || fullScore ? 'all' : activePartDefinition?.id;
  const auditionKey = `${score.id}:${auditionSource}:${firstAuditionBar}:${lastAuditionBar}`;
  const caretPlaybackKey = `${score.id}:${auditionSource}:from:${firstAuditionBar}:${caretBeat}:${caretPlaybackLastBar}`;
  const auditionLabel =
    firstAuditionBar === lastAuditionBar
      ? `Bar ${firstAuditionBar + 1}`
      : `Bars ${firstAuditionBar + 1}–${lastAuditionBar + 1}`;
  const activeWrittenPlaybackLabel =
    auditionSelection === auditionKey
      ? auditionLabel
      : auditionSelection === caretPlaybackKey
        ? passageIndices.length > 0
          ? auditionLabel
          : `From bar ${firstAuditionBar + 1}${caretBeat > 1 ? `, beat ${Number(caretBeat.toFixed(3))}` : ''}`
        : null;
  const playbackStatus =
    playbackScope === 'band'
      ? bandPlaybackStatus(band)
      : activeWrittenPlaybackLabel
        ? `${activeWrittenPlaybackLabel} · ${playbackScope === 'score' ? 'all written parts' : (activePartDefinition?.name ?? 'Lead')}`
        : playbackScope === 'score'
          ? 'Written score · all parts'
          : `Selected part · ${activePartDefinition?.name ?? 'Lead'}`;
  const writtenVisitLabel = playbackScope !== 'band' && score.parts.some((part) =>
    part.measures.some((measure) => measure.playback?.length),
  ) ? ' · first visit' : '';
  const audioEnabled = useStore((s) => s.audioEnabled);
  const stopScore = player.stop;

  useEffect(() => {
    if (
      auditionSelection &&
      auditionSelection !== auditionKey &&
      auditionSelection !== caretPlaybackKey
    ) {
      stopScore();
      setAuditionSelection(null);
    }
  }, [auditionKey, auditionSelection, caretPlaybackKey, stopScore]);

  const playWrittenFromCaret = () => {
    if (!caretPlaybackRange) return;
    setAuditionSelection(caretPlaybackKey);
    player.play(caretPlaybackRange);
  };
  const playBandFromCaret = () => {
    const measureId = score.form[firstAuditionBar]?.id;
    if (!measureId) return;
    store.getState().setPlayCursor(firstAuditionBar === 0 ? null : measureId);
    band.playAll({ startMeasureId: measureId, startMeasureBeat: caretBeat });
  };
  const auditionBars = () => {
    if (!audioEnabled || !auditionRange) return;
    // Band and part scopes already share the same written timeline. Switching
    // to its visible transport does not change the source beneath this request.
    if (playbackScope === 'band') setPlaybackScope(fullScore ? 'score' : 'part');
    setAuditionSelection(auditionKey);
    player.play(auditionRange);
    focusScore();
  };

  const focusScore = () => scoreSurfaceRef.current?.focus({ preventScroll: true });
  // Seed the visible written register, then store the cursor at concert pitch.
  const entrySeedMidi =
    (activeStaffDefinition?.clef === 'bass' ? 48 : 60) - (partTransposition?.semitones ?? 0);
  const [barJumpRequest, setBarJumpRequest] = useState(0);
  const jumpToBar = (barNumber: number) => {
    const measure = score.form[barNumber - 1];
    if (!Number.isInteger(barNumber) || !measure) return;
    const st = store.getState();
    st.selectNote(null);
    st.selectChord(null);
    st.clearPassage();
    st.selectMeasure(measure.id);
    st.setScoreEntryCursor(
      writeMode && editable
        ? { measureId: measure.id, beat: 1, midi: entryCursor?.midi ?? entrySeedMidi }
        : null,
    );
    setBarJumpRequest((request) => request + 1);
    focusScore();
  };
  const navigateFromOverview = (barIndex: number, extend: boolean) => {
    if (!extend) {
      jumpToBar(barIndex + 1);
      return;
    }
    const measure = score.form[barIndex];
    if (!measure) return;
    const st = store.getState();
    st.selectNote(null);
    st.selectChord(null);
    st.extendPassage(measure.id);
    st.selectMeasure(measure.id);
    st.setScoreEntryCursor(
      writeMode && editable
        ? { measureId: measure.id, beat: 1, midi: entryCursor?.midi ?? entrySeedMidi }
        : null,
    );
    setBarJumpRequest((request) => request + 1);
    focusScore();
  };
  const restoreScoreEdit = (direction: 'undo' | 'redo') => {
    const state = store.getState();
    if (direction === 'undo') state.scoreUndo();
    else state.scoreRedo();
    setBarJumpRequest((request) => request + 1);
    focusScore();
  };

  const selectAdjacentNote = (id: string) => {
    const state = store.getState();
    const event = describeEvent(state.score, id);
    if (!event) return;
    state.selectNote(id);
    state.selectChord(null);
    state.clearPassage();
    state.selectMeasure(state.score.form[event.measureIndex].id);
    setBarJumpRequest((request) => request + 1);
    focusScore();
  };

  const ensureEntryCursor = () => {
    const st = store.getState();
    if (!st.scoreEntryCursor && st.score.form[0]) {
      st.setScoreEntryCursor({ measureId: st.score.form[0].id, beat: 1, midi: entrySeedMidi });
    }
    return store.getState().scoreEntryCursor;
  };

  const activateWriteCursor = () => {
    const cursor = ensureEntryCursor();
    if (cursor) store.getState().setScoreEntryCursor(cursor);
  };

  const finishEntry = () => {
    setDeletedScore(null);
    setWriteMode(false);
    setShowPiano(false);
    setRestMode(false);
    setEntryAccidental('key');
    const st = store.getState();
    st.selectNote(null);
    st.selectChord(null);
    st.setScoreEntryCursor(null);
  };

  const deleteMobileNote = () => {
    const before = store.getState().score;
    store.getState().scoreDeleteSelected();
    const after = store.getState().score;
    if (before !== after && !store.getState().selectedNoteId) setDeletedScore(after);
  };

  const choosePart = (index: number) => {
    setPartPendingRemovalId(null);
    setRenamingPart(false);
    if (index < 0 && playbackScope === 'part') choosePlaybackScope('score');
    finishEntry();
    setArrangeMode(false);
    setPartIndex(index);
    if (index < 0) return;
    const part = store.getState().score.parts[index];
    if (part) store.getState().setScoreContentTarget({ partId: part.id, staffIndex: 0 });
  };

  const chooseStaff = (staffIndex: number) => {
    const state = store.getState();
    const part = state.score.parts[activePart];
    const staff = part?.staves[staffIndex];
    if (!part || !staff || fullScore) return;
    state.selectChord(null);
    state.setScoreContentTarget({ partId: part.id, staffIndex });
    if (writeMode && state.score.form[0]) {
      store.getState().setScoreEntryCursor({
        measureId: state.score.form[0].id,
        beat: 1,
        midi: (staff.clef === 'bass' ? 48 : 60) - (part.transposition?.semitones ?? 0),
      });
    }
  };

  const addScorePart = (preset: PartPreset) => {
    setPartPendingRemovalId(null);
    const partId = store.getState().scoreAddPart(preset);
    if (!partId) return;
    finishEntry();
    setArrangeMode(false);
    const index = store.getState().score.parts.findIndex((part) => part.id === partId);
    if (index >= 0) setPartIndex(index);
  };

  const renameActivePart = () => {
    setPartPendingRemovalId(null);
    const part = score.parts[activePart];
    if (!part || fullScore) return;
    setPartName(part.name);
    setRenamingPart(true);
  };

  const splitActivePart = () => {
    if (fullScore || !activePartDefinition || !staffSplitCapability.canSplit) return;
    if (!store.getState().scoreSplitPartStaves(activePartDefinition.id)) return;
    finishEntry();
    setArrangeMode(false);
    setRenamingPart(false);
    setPartPendingRemovalId(null);
    const state = store.getState();
    const index = state.score.parts.findIndex(
      (part) => part.id === state.scoreContentTarget?.partId,
    );
    if (index >= 0) setPartIndex(index);
  };

  const requestRemoveActivePart = () => {
    const part = score.parts[activePart];
    if (!part || fullScore) return;
    setPartPendingRemovalId(part.id);
  };

  const confirmRemovePart = () => {
    const partId = partPendingRemovalId;
    const state = store.getState();
    if (!partId || state.score.parts.length === 0) {
      setPartPendingRemovalId(null);
      return;
    }
    const part = state.score.parts.find((candidate) => candidate.id === partId);
    if (!part) {
      setPartPendingRemovalId(null);
      return;
    }
    state.scoreRemovePart(part.id);
    finishEntry();
    const target = store.getState().scoreContentTarget;
    const index = target
      ? store.getState().score.parts.findIndex((candidate) => candidate.id === target.partId)
      : 0;
    setPartIndex(Math.max(0, index));
    setPartPendingRemovalId(null);
  };

  const stepEntryCursor = (direction: -1 | 1, byMeasure = false, growAtEnd = true) => {
    if (!editable) return;
    ensureEntryCursor();
    store.getState().scoreStepEntryCursor(direction, byMeasure, growAtEnd);
  };

  const nudgeEntryCursor = (semitones: number) => {
    if (!editable) return;
    ensureEntryCursor();
    store.getState().scoreNudgeEntryCursor(semitones);
  };

  // The modifier keys/buttons serve two masters, split by MODE (MuseScore's
  // model): in Write mode they arm the NEXT entry — the toolbar state; in
  // Select mode with a note selected they APPLY to that note. Before this
  // split, pressing "." on a selected note just toggled a toolbar light.
  const chooseEntryDuration = (duration: number) => {
    if (!editable) return;
    const st = store.getState();
    if (!writeMode && st.selectedNoteId) st.scoreSetSelectedDuration(duration);
    else st.setEntryDuration(duration);
  };

  const toggleEntryDot = () => {
    if (!editable) return;
    const st = store.getState();
    if (!writeMode && st.selectedNoteId) st.scoreToggleSelectedDot();
    else st.setEntryDuration(toggleDottedDuration(st.entryDuration));
  };

  const chooseEntryAccidental = (accidental: EntryAccidental) => {
    if (!editable) return;
    if (writeMode) {
      setEntryAccidental(accidental);
      return;
    }
    // 'key' means "spell the next entry from the key signature" — there is
    // no next entry in select mode, so only the real inflections apply.
    const st = store.getState();
    if (accidental !== 'key' && st.selectedNoteId) st.scoreApplySelectedAccidental(accidental);
  };

  const toggleOutgoingTie = () => {
    if (!editable) return;
    const st = store.getState();
    if (st.selectedNoteId || st.scoreEntryCursor) st.scoreToggleOutgoingTie(writeMode);
  };

  const enterNoteForLetter = (letter: string) => {
    if (!editable || !writeMode) return;
    const cursor = ensureEntryCursor();
    const st = store.getState();
    if (!cursor) return;
    const transposition = partTransposition?.semitones ?? 0;
    const keySignature = writtenKeySignature(
      entryKeySignature(st.score, cursor.measureId),
      partTransposition,
    );
    const writtenSpelling = entrySpellingForLetter(letter, keySignature, entryAccidental);
    const writtenMidi = entryMidiForLetter(
      letter,
      cursor.midi + transposition,
      keySignature,
      entryAccidental,
    );
    let concertSpelling = writtenSpelling;
    if (partTransposition) {
      try {
        const writtenPitch = spellMidi(writtenMidi, { name: writtenSpelling });
        concertSpelling = noteNameOf(
          transposePitch(writtenPitch, invertInterval(partTransposition)),
        );
      } catch {
        concertSpelling = '';
      }
    }
    st.scoreEnterAtCursor(writtenMidi - transposition, false, concertSpelling || undefined);
    setEntryAccidental('key');
  };

  const enterRest = () => {
    if (!editable || !writeMode) return;
    const cursor = ensureEntryCursor();
    if (cursor) store.getState().scoreEnterAtCursor(cursor.midi, true);
  };

  const insertTripletAtCursor = () => {
    if (!editable || !writeMode) return;
    const cursor = ensureEntryCursor();
    if (cursor) store.getState().scoreInsertTripletAtCursor();
  };

  const togglePlayback = () => {
    if (!store.getState().audioEnabled) return;
    if (playbackScope === 'band') {
      if (band.transport === 'playing') getChordPlayer().pause();
      else if (band.transport === 'paused') void getChordPlayer().resumePlayback();
      else playBandFromCaret();
    } else if (player.transport === 'playing') player.pause();
    else if (player.transport === 'paused') player.resume();
    else playWrittenFromCaret();
  };

  useEffect(() => {
    if ((!canEditSecondVoice && entryVoice !== 0) || (entryVoice !== 0 && entryVoice !== 1)) {
      store.getState().setEntryVoice(0);
    }
  }, [canEditSecondVoice, entryVoice, store]);

  useEffect(() => {
    if (editable) return;
    if (writeMode) setWriteMode(false);
    if (restMode) setRestMode(false);
    if (entryAccidental !== 'key') setEntryAccidental('key');
    if (showPiano) setShowPiano(false);
    if (selectedId) store.getState().selectNote(null);
  }, [editable, entryAccidental, restMode, selectedId, showPiano, store, writeMode]);

  useEffect(() => {
    if (!writeMode && entryAccidental !== 'key') setEntryAccidental('key');
  }, [entryAccidental, writeMode]);

  useEffect(() => {
    const firstMeasure = score.form[0];
    if (!writeMode || !editable || entryCursor || !firstMeasure) return;
    store
      .getState()
      .setScoreEntryCursor({ measureId: firstMeasure.id, beat: 1, midi: entrySeedMidi });
  }, [editable, entryCursor, entrySeedMidi, score.form, store, writeMode]);

  const pasteScore = (mode: ClipboardPasteMode = 'all') => {
    const pasted = store.getState().scorePaste(mode);
    setClipboardNotice(
      pasted ? '' : 'That clipboard content does not fit at the current score position.',
    );
    return pasted;
  };

  const copyScore = () => {
    store.getState().scoreCopy();
    setClipboardNotice('');
  };

  const cutScore = () => {
    const cut = store.getState().scoreCut();
    setClipboardNotice(cut ? '' : 'Select a note or passage to cut.');
    return cut;
  };

  const onScoreKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const target = e.target as Element | null;
    if (target?.closest('input, textarea, select, button, [contenteditable="true"]')) return;

    const key = e.key.toLowerCase();
    let st = store.getState();
    if (e.key === 'Escape') {
      e.preventDefault();
      finishEntry();
      st.clearPassage();
      return;
    }

    if ((e.metaKey || e.ctrlKey) && !e.shiftKey && key === 'c') {
      if (chordEditorVisible) return;
      if (window.getSelection()?.toString()) return;
      e.preventDefault();
      copyScore();
      return;
    }
    if ((e.metaKey || e.ctrlKey) && !e.shiftKey && key === 'x') {
      if (fullScore || chordEditorVisible || !editable) return;
      e.preventDefault();
      cutScore();
      return;
    }
    if ((e.metaKey || e.ctrlKey) && !e.shiftKey && key === 'v') {
      if (fullScore || chordEditorVisible) return;
      e.preventDefault();
      pasteScore();
      return;
    }

    if ((e.metaKey || e.ctrlKey) && key === 'z') {
      if (fullScore && !chordEditorVisible) return;
      e.preventDefault();
      restoreScoreEdit(e.shiftKey ? 'redo' : 'undo');
      return;
    }
    if ((e.metaKey || e.ctrlKey) && !e.shiftKey && key === 'y') {
      if (fullScore && !chordEditorVisible) return;
      e.preventDefault();
      restoreScoreEdit('redo');
      return;
    }

    if (!e.metaKey && !e.ctrlKey && !e.altKey && e.key === ' ') {
      e.preventDefault();
      if (e.repeat) return;
      togglePlayback();
      return;
    }

    const barNavigationActive =
      arrangeMode ||
      passageIdSet.size > 0 ||
      (!writeMode && !entryCursor && !selectedId && !selectedChordId && selectedMeasureId != null);
    if (
      barNavigationActive &&
      !e.metaKey &&
      !e.ctrlKey &&
      !e.altKey &&
      (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End')
    ) {
      e.preventDefault();
      const targetIndex =
        e.key === 'Home'
          ? 0
          : e.key === 'End'
            ? score.form.length - 1
            : Math.max(
                0,
                Math.min(
                  score.form.length - 1,
                  currentBarIndex + (e.key === 'ArrowRight' ? 1 : -1),
                ),
              );
      navigateFromOverview(targetIndex, e.shiftKey);
      return;
    }

    if (!editable) return;

    if (!e.metaKey && !e.ctrlKey && !e.altKey && key === 'n') {
      e.preventDefault();
      if (e.repeat) return;
      const next = !writeMode;
      setWriteMode(next);
      if (next) {
        st.selectChord(null);
        setArrangeMode(false);
        activateWriteCursor();
      } else setEntryAccidental('key');
      return;
    }

    if (arrangeMode || chordEditorVisible) return;

    if (!e.altKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
      e.preventDefault();
      stepEntryCursor(e.key === 'ArrowRight' ? 1 : -1, e.metaKey || e.ctrlKey, !e.repeat);
      return;
    }

    if (!e.metaKey && !e.ctrlKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      e.preventDefault();
      const semitones = (e.key === 'ArrowUp' ? 1 : -1) * (e.shiftKey ? 12 : 1);
      nudgeEntryCursor(semitones);
      return;
    }

    if (e.key === 'Backspace' || e.key === 'Delete') {
      if (!st.selectedNoteId) return;
      e.preventDefault();
      st.scoreDeleteSelected();
      return;
    }

    if (writeMode && !e.altKey && (e.metaKey || e.ctrlKey) && e.key === '3') {
      e.preventDefault();
      if (!e.repeat) insertTripletAtCursor();
      return;
    }

    if (!e.metaKey && !e.ctrlKey && !e.altKey && DURATION_SHORTCUTS[e.key]) {
      e.preventDefault();
      chooseEntryDuration(DURATION_SHORTCUTS[e.key]);
      return;
    }

    if (!e.metaKey && !e.ctrlKey && !e.altKey && e.key === '.') {
      e.preventDefault();
      if (e.repeat) return;
      toggleEntryDot();
      return;
    }

    if (!e.metaKey && !e.ctrlKey && !e.altKey && (key === 't' || e.key === '+')) {
      e.preventDefault();
      if (e.repeat) return;
      toggleOutgoingTie();
      return;
    }

    // Not write-gated: in select mode these APPLY to the selected note
    // (chooseEntryAccidental routes by mode).
    if (!e.metaKey && !e.ctrlKey && !e.altKey) {
      const accidental: EntryAccidental | undefined =
        key === 'k'
          ? 'key'
          : e.key === '='
            ? 'natural'
            : e.key === '#'
              ? 'sharp'
              : e.key === '-'
                ? 'flat'
                : undefined;
      if (accidental) {
        e.preventDefault();
        if (!e.repeat) chooseEntryAccidental(accidental);
        return;
      }
    }

    if (writeMode && !e.metaKey && !e.ctrlKey && !e.altKey && /^[a-g]$/.test(key)) {
      e.preventDefault();
      if (e.repeat) return;
      enterNoteForLetter(key);
      return;
    }

    if (writeMode && !e.metaKey && !e.ctrlKey && !e.altKey && (e.key === '0' || key === 'r')) {
      e.preventDefault();
      if (e.repeat) return;
      enterRest();
      return;
    }

    if (writeMode && !e.metaKey && !e.ctrlKey && !e.altKey && e.key === 'Enter') {
      e.preventDefault();
      if (e.repeat) return;
      const cursor = ensureEntryCursor();
      st = store.getState();
      if (cursor) st.scoreEnterAtCursor(cursor.midi);
      return;
    }

    // MuseScore uses J for enharmonic respelling. R remains a compatibility
    // alias while not writing; in Write mode it is the Guitar Pro rest key.
    if (!e.metaKey && !e.ctrlKey && (key === 'j' || (!writeMode && key === 'r'))) {
      if (!st.selectedNoteId) return;
      e.preventDefault();
      if (e.repeat) return;
      st.scoreRespellSelected();
      return;
    }
  };

  const drawnEntryCursor = useMemo(() => {
    if (!entryCursor || !editable) return null;
    const barIndex = score.form.findIndex((measure) => measure.id === entryCursor.measureId);
    return barIndex >= 0 ? { barIndex, beat: entryCursor.beat } : null;
  }, [editable, entryCursor, score.form]);

  // selectedMeasureId's place in the scroll context below.
  const SELECTED_MEASURE = 3;
  const previousScrollContext = useRef([
    entryCursor,
    selectedId,
    selectedChordId,
    selectedMeasureId,
    activePart,
    activeStaffIndex,
    barJumpRequest,
    contextEditorVisible,
  ]);
  useEffect(() => {
    const context = [
      entryCursor,
      selectedId,
      selectedChordId,
      selectedMeasureId,
      activePart,
      activeStaffIndex,
      barJumpRequest,
      contextEditorVisible,
    ];
    // Preserve the selection when returning to Studio, but let the workspace
    // open at its heading. Scroll only when an editing action changes context.
    const previous = previousScrollContext.current;
    if (context.every((value, index) => value === previous[index])) return;
    previousScrollContext.current = context;
    // The form editor selects bar 1 by itself when the Studio opens with
    // nothing selected. That is a default, not an edit: scrolling to it
    // lifted the bar clear of the transport and tucked the masthead and the
    // tempo heading under the sticky dock.
    const changed = context.flatMap((value, index) => (value === previous[index] ? [] : [index]));
    if (
      changed.length === 1 &&
      changed[0] === SELECTED_MEASURE &&
      previous[SELECTED_MEASURE] == null &&
      selectedMeasureId === score.form[0]?.id
    )
      return;
    if (typeof requestAnimationFrame !== 'function') return;
    const frame = requestAnimationFrame(() => {
      const surface = scoreSurfaceRef.current;
      const caret =
        (entryCursor && surface?.querySelector('[data-entry-cursor]')) ||
        (selectedId &&
          Array.from(surface?.querySelectorAll('[data-note]') ?? []).find(
            (note) => note.getAttribute('data-note') === selectedId,
          )) ||
        (selectedChordId &&
          Array.from(surface?.querySelectorAll('[data-chord-id]') ?? []).find(
            (chord) => chord.getAttribute('data-chord-id') === selectedChordId,
          )) ||
        (selectedMeasureId &&
          Array.from(surface?.querySelectorAll('[data-measure-id]') ?? []).find(
            (measure) => measure.getAttribute('data-measure-id') === selectedMeasureId,
          ));
      if (caret && 'scrollIntoView' in caret) {
        (
          caret as Element & { scrollIntoView: (options?: ScrollIntoViewOptions) => void }
        ).scrollIntoView({ block: 'nearest', inline: 'nearest' });
        const view = caret.ownerDocument.defaultView;
        if (!view) return;
        const rect = caret.getBoundingClientRect();
        const topbarBottom =
          caret.ownerDocument.querySelector('.topbar')?.getBoundingClientRect().bottom ?? 0;
        const dockBottom =
          caret.ownerDocument.querySelector('.score-editor-dock')?.getBoundingClientRect().bottom ??
          topbarBottom;
        const transportTop =
          caret.ownerDocument.querySelector('.transport-bar')?.getBoundingClientRect().top ??
          view.innerHeight;
        const noteEditorTop =
          caret.ownerDocument
            .querySelector('.score-mobile-note-editor, .score-chord-editor')
            ?.getBoundingClientRect().top ?? view.innerHeight;
        const visibleTop = Math.max(topbarBottom, dockBottom) + 12;
        const visibleBottom = Math.min(view.innerHeight, transportTop, noteEditorTop) - 12;
        if (rect.top < visibleTop || rect.height > visibleBottom - visibleTop) {
          view.scrollBy(0, rect.top - visibleTop);
        } else if (rect.bottom > visibleBottom) view.scrollBy(0, rect.bottom - visibleBottom);
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [
    entryCursor,
    selectedId,
    selectedChordId,
    selectedMeasureId,
    activePart,
    activeStaffIndex,
    barJumpRequest,
    contextEditorVisible,
    score.form,
  ]);

  // The band's playhead on the written score; the score-preview player's own
  // playhead wins while it runs.
  const bandPlayheadBeat = useBandPlayheadBeat(band, bars);
  const scorePlayheadBeat =
    player.playheadBeat == null ? bandPlayheadBeat : barGridBeat(timeline, player.playheadBeat);
  const playingBarIndex = useMemo(() => {
    if (scorePlayheadBeat == null) return null;
    let start = 0;
    for (let index = 0; index < bars.length; index += 1) {
      const end = start + bars[index].beats;
      if (scorePlayheadBeat >= start && scorePlayheadBeat < end) return index;
      start = end;
    }
    return null;
  }, [bars, scorePlayheadBeat]);

  useEffect(() => {
    const surface = scoreSurfaceRef.current;
    const view = surface?.ownerDocument.defaultView;
    if (!surface || !view || typeof requestAnimationFrame !== 'function') return;
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const dockBottom =
          surface.ownerDocument
            .querySelector('.score-editor-dock')
            ?.getBoundingClientRect().bottom ?? 0;
        const transportTop =
          surface.ownerDocument.querySelector('.transport-bar')?.getBoundingClientRect().top ??
          view.innerHeight;
        const visible = new Set<string>();
        surface.querySelectorAll<SVGElement>('[data-measure-id]').forEach((measure) => {
          const rect = measure.getBoundingClientRect();
          if (
            rect.width > 0 &&
            rect.height > 0 &&
            rect.bottom > dockBottom &&
            rect.top < transportTop &&
            rect.right > 0 &&
            rect.left < view.innerWidth
          ) {
            const id = measure.getAttribute('data-measure-id');
            if (id) visible.add(id);
          }
        });
        setVisibleMeasureIds((previous) => {
          if (
            previous.size === visible.size &&
            [...previous].every((measureId) => visible.has(measureId))
          ) {
            return previous;
          }
          return visible;
        });
      });
    };
    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    resizeObserver?.observe(surface);
    if (editorDockRef.current) resizeObserver?.observe(editorDockRef.current);
    view.addEventListener('resize', update);
    view.addEventListener('scroll', update, true);
    update();
    return () => {
      cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      view.removeEventListener('resize', update);
      view.removeEventListener('scroll', update, true);
    };
  }, [activePart, activeStaffIndex, fullScore, pager.paged, score.form, systemWidth]);

  const place = (c: StaffClick) => {
    focusScore();
    if (!writeMode) {
      store.getState().selectNote(null);
      store.getState().setScoreEntryCursor(null);
      store.getState().selectChord(null);
      store.getState().clearPassage();
      store.getState().selectMeasure(score.form[c.barIndex]?.id ?? null);
      return;
    }
    // ScoreView only emits entry hits for the chosen staff. Keep the model
    // seam defensive so a stale render can never write into another staff.
    if (c.staffIndex !== activeStaffIndex) return;
    const bar = bars[c.barIndex];
    const measureId = score.form[c.barIndex]?.id;
    if (!bar || !measureId) return;
    const beat = snapBeat(
      c.beatFraction,
      bar.beats,
      entryGridInBeats(score, measureId, entryDuration),
    );
    // scoreToSong maps form measures 1:1 onto bars, so the global bar index IS
    // the measure index.
    store.getState().scoreAddNote(c.barIndex, {
      // scoreToStaffSong displays a transposing part at written pitch; the
      // score model stores concert pitch.
      midi: c.midi - (partTransposition?.semitones ?? 0),
      beat,
      duration: entryDurationInBeats(score, measureId, entryDuration),
      ...(restMode ? { rest: true } : {}),
    });
  };

  const doExport = () => {
    const snapshot = store.getState();
    const { xml } = snapshot.exportMusicXML();
    const title = snapshot.score.title?.trim() || snapshot.chart.title?.trim() || 'score';
    const filename =
      title
        .replace(/[<>:"/\\|?*]/g, '-')
        .replace(/\s+/g, ' ')
        .replace(/[. ]+$/g, '') || 'score';
    const url = URL.createObjectURL(
      new Blob([xml], {
        type: 'application/vnd.recordare.musicxml+xml;charset=utf-8',
      }),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.musicxml`;
    a.hidden = true;
    document.body.appendChild(a);
    try {
      a.click();
    } finally {
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    }
  };

  /** MusicXML, or ABC: one tune, or a tunebook whose first tune opens. */
  const importScoreText = (text: string, fileName?: string): boolean => {
    if (isAbcSource(text)) {
      const res = store.getState().importAbc(text, fileName);
      if (!res.ok) {
        window.alert(`Import failed: ${res.error}`);
        return false;
      }
      if (res.tunes > 1) {
        const book = store.getState().playlist?.name;
        setImportNotice(`${book ? `${book} · ` : ''}${res.tunes} tunes`);
      }
      return true;
    }
    const res = store.getState().importMusicXML(text);
    if (!res.ok) window.alert(`Import failed: ${res.error}`);
    return res.ok;
  };

  const doPasteImport = () => {
    if (importScoreText(pasteText)) {
      setPasteText('');
      setShowPaste(false);
    }
  };

  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    readScoreFile(file)
      .then((text) => {
        importScoreText(text, file.name);
      })
      .catch((err: unknown) => {
        window.alert(`Import failed: ${err instanceof Error ? err.message : 'unreadable file'}`);
      });
    e.target.value = '';
  };

  const startBlankScore = (): boolean => {
    const current = store.getState().score;
    const hasContent =
      current.form.length > 1 ||
      current.form.some((measure) => measure.harmony.length > 0) ||
      current.parts.some((part) =>
        part.measures.some((measure) => measure.voices.some((voice) => voice.items.length > 0)),
      );
    if (hasContent && !window.confirm('Start a blank score? This replaces the current score.')) {
      return false;
    }
    finishEntry();
    setArrangeMode(false);
    setPartIndex(0);
    setShowPaste(false);
    store.getState().newTune({});
    return true;
  };

  const loadScoreExample = (id: string) => {
    if (id === 'lead-sheet') {
      store.getState().loadSong(SAMPLE);
      return;
    }
    if (id.startsWith('native:')) {
      const entry = BUNDLED_SCORES.find((candidate) => `native:${candidate.record.id}` === id);
      if (!entry) return;
      loadBundledScore(entry)
        .then((nativeScore) => store.getState().loadScore(nativeScore))
        .catch((err: unknown) => {
          window.alert(
            `Score failed to load: ${err instanceof Error ? err.message : 'unreadable score'}`,
          );
        });
      return;
    }
    if (id.startsWith('bundled:')) {
      const entry = BUNDLED_SAMPLES.find((sample) => `bundled:${sample.id}` === id);
      if (!entry) return;
      fetch(`${import.meta.env.BASE_URL}samples/${entry.file}`)
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then((xml) => {
          const result = store.getState().importMusicXML(xml);
          if (!result.ok) window.alert(`Sample failed to load: ${result.error}`);
        })
        .catch((err: unknown) => {
          window.alert(
            `Sample failed to load: ${err instanceof Error ? err.message : 'fetch failed'}`,
          );
        });
      return;
    }
    if (id.startsWith('openbook:')) {
      const entry = openbookLib.find((tune) => `openbook:${tune.id}` === id);
      if (!entry) return;
      loadOpenbookScore(entry)
        .then((nativeScore) => store.getState().loadScore(nativeScore))
        .catch((err: unknown) => {
          window.alert(
            `Tune failed to load: ${err instanceof Error ? err.message : 'fetch failed'}`,
          );
        });
      return;
    }
    if (id.startsWith('sample:')) {
      const entry = samples.find((sample) => `sample:${sample.name}` === id);
      if (!entry) return;
      entry
        .read()
        .then((xml) => {
          const result = store.getState().importMusicXML(xml);
          if (!result.ok) window.alert(`Sample failed to load: ${result.error}`);
        })
        .catch((err: unknown) => {
          window.alert(
            `Sample failed to load: ${err instanceof Error ? err.message : 'unreadable file'}`,
          );
        });
      return;
    }
    const example = SCORE_EXAMPLES.find((candidate) => candidate.id === id);
    if (!example) return;
    const result = store.getState().importMusicXML(example.xml);
    if (!result.ok) window.alert(`Example failed to load: ${result.error}`);
  };

  const partPendingRemoval = partPendingRemovalId
    ? score.parts.find((part) => part.id === partPendingRemovalId)
    : undefined;

  const compactPartOptions = compact && score.parts.length <= 1 && !hasMultipleStaves;
  const renderFileActions = (closeFile: () => void) => (
    <div className="score-file-menu" data-testid="score-file-actions">
      <button
        type="button"
        className="score-file-close"
        hidden={compact}
        aria-label="Close file menu"
        onClick={closeFile}
      >
        Close
      </button>
      <div className="score-file-menu-group">
        <span className="score-file-menu-label">New or open</span>
        <button
          type="button"
          className="btn btn-sm"
          data-testid="score-new-blank"
          onClick={() => {
            if (startBlankScore()) closeFile();
          }}
        >
          New blank score
        </button>
        <select
          data-testid="score-examples"
          aria-label="Open a score or example"
          title="Open a bundled score, tune, notation example or MusicXML sample"
          value=""
          onChange={(event) => {
            loadScoreExample(event.target.value);
            closeFile();
          }}
        >
          <option value="">Open score or example…</option>
          <optgroup label="Scores">
            {BUNDLED_SCORES.map((entry) => (
              <option key={entry.record.id} value={`native:${entry.record.id}`}>
                {entry.summary.title}
                {entry.summary.composer ? ` — ${entry.summary.composer}` : ''}
              </option>
            ))}
          </optgroup>
          <optgroup label="Notation examples">
            <option value="lead-sheet">Lead sheet sample</option>
            {SCORE_EXAMPLES.map((example) => (
              <option key={example.id} value={example.id}>
                {example.label}
              </option>
            ))}
          </optgroup>
          <optgroup label="Lead sheets">
            {openbookOptions.map((tune) => (
              <option key={tune.value} value={tune.value}>
                {tune.label}
              </option>
            ))}
          </optgroup>
          <optgroup label="MusicXML samples">
            {BUNDLED_SAMPLES.map((sample) => (
              <option key={sample.id} value={`bundled:${sample.id}`}>
                {sample.label}
              </option>
            ))}
          </optgroup>
          {samples.length > 0 && (
            <optgroup label={samplesFolder ?? 'Your folder'}>
              {samples.map((sample) => (
                <option key={sample.name} value={`sample:${sample.name}`}>
                  {sample.name}
                </option>
              ))}
            </optgroup>
          )}
        </select>
        <button
          type="button"
          className="btn btn-sm"
          data-testid="samples-folder"
          title="Add a local folder of .musicxml and .mxl files to the score picker"
          onClick={connectSamplesFolder}
        >
          {samplesPending
            ? `Reconnect ${samplesFolder ?? 'folder'}…`
            : samplesFolder
              ? `Folder: ${samplesFolder}`
              : 'Connect samples folder…'}
        </button>
      </div>

      <div className="score-file-menu-group">
        <span className="score-file-menu-label">Import</span>
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => {
            fileRef.current?.click();
            closeFile();
          }}
        >
          Import file…
        </button>

        <button
          type="button"
          className="btn btn-sm"
          aria-pressed={showPaste}
          onClick={() => {
            setShowPaste((visible) => !visible);
            closeFile();
          }}
        >
          Paste MusicXML or ABC…
        </button>
        <button
          type="button"
          className="btn btn-sm"
          data-testid="score-tidy"
          title="Clean up an AI transcription: detect the key and respell, re-bar when the evidence is decisive, quantise rhythm dust, detect the chord changes and part roles. One undoable edit."
          onClick={() => {
            finishEntry();
            const report = store.getState().tidyScoreTranscription();
            closeFile();
            if (!report.changed) {
              const why = report.skipped.length ? ` — ${report.skipped.join('; ')}` : '';
              window.alert(`Nothing to tidy${why}.`);
            }
          }}
        >
          Tidy transcription
        </button>
      </div>

      <div className="score-file-menu-group">
        <span className="score-file-menu-label">Export</span>
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => {
            doExport();
            closeFile();
          }}
        >
          Export MusicXML
        </button>
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => {
            closeFile();
            window.print();
          }}
        >
          Print score…
        </button>
      </div>

      <div className="score-file-menu-group score-file-menu-danger">
        <span className="score-file-menu-label">Reset content</span>
        <button
          type="button"
          className="btn btn-sm btn-danger"
          title="Remove every note and chord; Undo restores them"
          disabled={!editable}
          onClick={() => {
            store.getState().scoreClearAll();
            closeFile();
          }}
        >
          Clear notes &amp; chords
        </button>
      </div>
    </div>
  );
  const partControls = (
    <div
      className="score-control-row score-part-controls"
      role="group"
      aria-label="Score parts"
      data-testid="score-part-controls"
    >
      <span className="score-control-label">Part</span>
      <label className="score-inline-select score-part-picker">
        <span className="visually-hidden">Selected part</span>
        <select
          data-testid="part-picker"
          value={fullScore ? -1 : activePart}
          onChange={(event) => choosePart(Number(event.target.value))}
          title="Choose the authored instrument to view and edit, or view the complete score"
        >
          {score.parts.length === 0 && <option value={0}>No notation part</option>}
          {score.parts.map((part, index) => (
            <option key={part.id} value={index}>
              {part.name || `Part ${index + 1}`}
            </option>
          ))}
          {score.parts.length > 1 && <option value={-1}>Full score (view)</option>}
        </select>
      </label>
      <details className="score-part-details" open={compactPartOptions || undefined}>
        <summary title="Manage parts" hidden={compactPartOptions}>
          Manage parts
        </summary>
        <div className="score-part-details-body">
          <label className="score-inline-select score-part-add-picker">
            <span className="muted">Add</span>
            <select
              data-testid="score-add-part"
              value=""
              aria-label="Add part"
              title="Add an authored instrument to the score"
              onChange={(event) => {
                const preset = event.target.value as PartPreset;
                if (PART_PRESET_OPTIONS.some((option) => option.value === preset)) {
                  addScorePart(preset);
                }
              }}
            >
              <option value="">Choose…</option>
              {PART_PRESET_OPTIONS.map((option) => (
                <option key={option.value} value={option.value} title={option.description}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          {renamingPart && activePartDefinition && (
            <form
              className="score-part-rename-form"
              onSubmit={(event) => {
                event.preventDefault();
                if (!partName.trim()) return;
                store.getState().scoreRenamePart(activePartDefinition.id, partName.trim());
                setRenamingPart(false);
              }}
            >
              <label>
                Part name
                <input
                  autoFocus
                  value={partName}
                  onChange={(event) => setPartName(event.target.value)}
                />
              </label>
              <button type="submit" disabled={!partName.trim()}>
                Save name
              </button>
              <button type="button" onClick={() => setRenamingPart(false)}>
                Cancel
              </button>
            </form>
          )}
          {partPendingRemoval ? (
            <div
              className="score-part-remove-confirm"
              role="group"
              aria-label={`Confirm removal of ${partPendingRemoval.name}`}
            >
              <span className="score-part-remove-message">
                <strong>Remove “{partPendingRemoval.name}”?</strong>{' '}
                <span className="muted">
                  {score.parts.length === 1
                    ? 'Its notation will be deleted; the chord form will remain.'
                    : 'All notation written for this part will be deleted.'}{' '}
                  Undo can restore it.
                </span>
              </span>
              <button
                type="button"
                data-testid="score-cancel-remove-part"
                autoFocus
                onClick={() => setPartPendingRemovalId(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="score-confirm-remove-part"
                data-testid="score-confirm-remove-part"
                onClick={confirmRemovePart}
              >
                Remove part
              </button>
            </div>
          ) : (
            <div
              className="score-part-manage-actions"
              role="group"
              aria-label="Manage selected part"
            >
              <button
                type="button"
                data-testid="score-rename-part"
                disabled={fullScore || score.parts.length === 0}
                onClick={renameActivePart}
              >
                Rename…
              </button>
              <button
                type="button"
                data-testid="score-remove-part"
                disabled={fullScore || score.parts.length === 0}
                onClick={requestRemoveActivePart}
              >
                Remove…
              </button>
            </div>
          )}
          {!fullScore && hasMultipleStaves && !partPendingRemoval && (
            <div className="score-part-split-actions">
              <button
                type="button"
                data-testid="score-split-part"
                disabled={!staffSplitCapability.canSplit}
                aria-describedby="score-part-split-description"
                onClick={splitActivePart}
              >
                Split into lead and bass
              </button>
              <p id="score-part-split-description" className="muted">
                {staffSplitCapability.canSplit
                  ? 'Separate the treble and bass staves into two parts. Keeps every voice and pitch. Undo restores the original part.'
                  : staffSplitCapability.reason}
              </p>
            </div>
          )}
        </div>
      </details>
    </div>
  );

  const staffControls = !fullScore && hasMultipleStaves && activePartDefinition && (
    <div
      className="btn-group"
      role="group"
      aria-label={pianoHandControls ? 'Piano staff' : `${activePartDefinition.name} staff`}
    >
      {activePartDefinition.staves.map((staff, staffIndex) => {
        const label = pianoHandControls
          ? staffIndex === 0
            ? 'RH'
            : 'LH'
          : `Staff ${staffIndex + 1}`;
        return (
          <button
            key={staff.id}
            type="button"
            aria-pressed={activeStaffIndex === staffIndex}
            onClick={() => chooseStaff(staffIndex)}
            title={
              pianoHandControls
                ? `Edit the piano ${staffIndex === 0 ? 'right' : 'left'}-hand staff`
                : `Edit ${activePartDefinition.name} staff ${staffIndex + 1}`
            }
          >
            {label}
          </button>
        );
      })}
    </div>
  );

  const expressionControls = selectedEvent && selectedExpression && (
    <ScoreExpressionControls
      rest={selectedEvent.rest}
      articulations={selectedExpression.articulations}
      dynamic={selectedExpression.dynamic}
      onToggleArticulation={(mark) => store.getState().scoreToggleSelectedArticulation(mark)}
      onDynamic={(dynamic) => store.getState().scoreSetSelectedDynamic(dynamic)}
    />
  );

  // Every page engraves the whole score, so its system breaks agree.
  // ScoreView maps pointers in whole-strip coordinates, and the entry
  // caret draws inside its own system's group, so it shows up on exactly
  // the page that holds it. Editing needs no page awareness at all.
  const renderScoreView = (systemWindow?: readonly [number, number], first = true) => (
    <ScoreView
      barNumbers={showBarNumbers}
      systemWindow={systemWindow}
      onSystemSpans={first ? pager.onSystemSpans : undefined}
      song={song}
      options={engraveOptions}
      {...analysisLane}
      selectedId={editable && !writeMode && !arrangeMode ? selectedId : null}
      selectedHeadIndex={selectedHeadIndex}
      selectedMeasureId={arrangeMode || (!selectedId && !writeMode) ? selectedMeasureId : null}
      selectedMeasureIds={passageIdSet}
      selectedChordId={!writeMode ? selectedChordId : null}
      entryCursor={drawnEntryCursor}
      onStaffClick={editable && !arrangeMode ? place : undefined}
      interactiveStaffIndex={activeStaffIndex}
      onSelectPassage={onSelectPassage}
      onClearPassage={onClearPassage}
      onSelectMeasure={
        arrangeMode
          ? (id) => {
              focusScore();
              const state = store.getState();
              state.selectNote(null);
              state.selectChord(null);
              state.selectMeasure(id);
            }
          : undefined
      }
      onSelectChord={(id) => {
        const state = store.getState();
        const selected = findChord(state.score, id);
        if (!selected) return;
        setWriteMode(false);
        setShowPiano(false);
        setDeletedScore(null);
        state.selectNote(null);
        state.setScoreEntryCursor(null);
        state.clearPassage();
        state.selectMeasure(state.score.form[selected.measureIndex].id);
        state.selectChord(id);
        setBarJumpRequest((request) => request + 1);
        focusScore();
      }}
      onSelectNote={
        editable && !writeMode && !arrangeMode
          ? (id, event) => {
              focusScore();
              const state = store.getState();
              const part = state.score.parts[activePart];
              if (!part) return;
              // Imported notes may be inside tuplets. Use the same event lookup
              // as keyboard editing so clicking reaches every authored note.
              const selected = describeEvent(state.score, id, {
                partId: part.id,
                staffIndex: activeStaffIndex,
              });
              if (!selected) return;
              const measureIndex = selected.measureIndex;
              state.selectNote(id, event?.headIndex);
              const measureId = state.score.form[measureIndex]?.id ?? null;
              if (measureId) {
                state.selectChord(null);
                if (event?.shiftKey) state.extendPassage(measureId);
                else state.clearPassage();
                state.selectMeasure(measureId);
              }
            }
          : undefined
      }
      touchNoteTargets={editable && !writeMode && !arrangeMode}
      touchChordTargets={compact && !arrangeMode}
      extraStaves={fullScore && fullStaves ? fullStaves.slice(1) : activeExtraStaves}
      staffMeta={
        fullScore && fullStaves
          ? fullStaves.map((e) => e.meta)
          : hasMultipleStaves && activePartDefinition
            ? activePartDefinition.staves.map(() => ({ group: activePartDefinition.id }))
            : undefined
      }
      playheadBeat={scorePlayheadBeat}
    />
  );

  return (
    <section
      ref={pageRef}
      className={`page score-page has-transport-bar${showPiano ? ' has-piano' : ''}${chordEditorVisible ? ' has-chord-editor' : ''}`}
      data-testid="score-page"
      data-playhead={playheadTint}
    >
      {/* File pickers must outlive the menu that opened them. */}
      <input
        ref={dirRef}
        type="file"
        multiple
        onChange={onSamplesDir}
        style={{ display: 'none' }}
        {...{ webkitdirectory: '' }}
      />
      <input
        ref={fileRef}
        type="file"
        accept=".xml,.musicxml,.mxl,.abc,.abct,text/xml,application/xml,text/vnd.abc"
        onChange={onFile}
        style={{ display: 'none' }}
      />
      <TuneWorkspaceHeader
        mode="edit"
        fileActions={renderFileActions}
        playbackStatus={playbackStatus + writtenVisitLabel}
      />
      {importNotice && <Toast testId="score-import-notice">{importNotice}</Toast>}

      <div
        ref={editorDockRef}
        className="score-editor-dock no-print"
        data-arrange-mode={arrangeMode ? 'true' : 'false'}
      >
        <div
          className="score-toolbar"
          role="toolbar"
          aria-label="Score editing"
          onClick={(event) => {
            // Pointer users expect the next shortcut to work immediately;
            // keyboard-activated buttons must retain focus for toolbar use.
            const target = event.target as Element;
            if (
              event.detail > 0 &&
              target.closest('button') &&
              !target.closest(
                '.score-file-popover, .score-part-controls, .score-expression-controls',
              )
            ) {
              focusScore();
            }
          }}
        >
          <div
            className="btn-group score-toolbar-durations"
            role="group"
            aria-label="Note duration"
            hidden={compactNoteEditor || !editable || arrangeMode || (!writeMode && !selectedEvent)}
          >
            {DURATIONS.map((d) => (
              <button
                key={d.beats}
                type="button"
                aria-label={d.label}
                aria-keyshortcuts={d.shortcut}
                data-mobile-label={d.mobileLabel}
                aria-pressed={Math.abs(displayDuration - d.beats) < 1e-9}
                disabled={!editable || selectedFixedDuration}
                title={d.shortcut ? `${d.label} (${d.shortcut})` : d.label}
                onClick={() => chooseEntryDuration(d.beats)}
                style={{ fontWeight: displayDuration === d.beats ? 600 : 400 }}
              >
                {d.label}
                {d.shortcut && (
                  <span className="score-duration-key" aria-hidden="true">
                    {d.shortcut}
                  </span>
                )}
              </button>
            ))}
          </div>
          <div
            className="btn-group score-mode-buttons"
            role="group"
            aria-label="Score editing mode"
          >
            <button
              type="button"
              data-testid="score-select-mode"
              aria-label="Select notes and chords"
              aria-keyshortcuts="Escape"
              aria-pressed={!writeMode && !arrangeMode}
              disabled={!editable && !score.form.some((measure) => measure.harmony.length > 0)}
              title="Select and edit existing notes or chord symbols"
              onClick={() => {
                finishEntry();
                setArrangeMode(false);
              }}
            >
              Select
            </button>
            <button
              type="button"
              className="score-write-button"
              data-testid="score-write-mode"
              aria-label="Write notes"
              aria-keyshortcuts="N"
              aria-pressed={writeMode}
              disabled={!editable}
              title={
                editable
                  ? 'Keyboard note entry (N): A-G notes, #/-/= accidentals, 0/R rest, T/+ tie, Ctrl/⌘+3 triplet, arrows move'
                  : fullScore
                    ? 'Choose one part to edit its notes'
                    : 'Grand-staff editing is not enabled yet; choose a single-staff part'
              }
              onClick={() => {
                store.getState().selectChord(null);
                setWriteMode(true);
                setArrangeMode(false);
                activateWriteCursor();
              }}
            >
              {compact ? 'Write' : 'Write notes'}
            </button>
            <button
              type="button"
              className="score-arrange-button"
              data-testid="score-arrange-mode"
              aria-label="Edit bars and chords"
              aria-pressed={arrangeMode}
              title="Select bars on the notation to edit chords, repeats, endings and navigation"
              onClick={() => {
                finishEntry();
                setArrangeMode(true);
                window.requestAnimationFrame(() =>
                  document
                    .getElementById('score-form-editor-panel')
                    ?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }),
                );
              }}
            >
              {compact ? 'Bars' : 'Bars & chords'}
            </button>
          </div>
          <div
            className="score-control-row score-note-controls"
            role="group"
            aria-label="Note tools"
            data-testid="score-note-controls"
            hidden={contextEditorVisible}
            data-empty={
              !writeMode &&
              !selectedId &&
              !canUndo &&
              !canRedo &&
              !hasClipboard &&
              passageIdSet.size === 0 &&
              !hasMultipleStaves
            }
          >
            <span className="score-control-label">
              {writeMode || selectedEvent ? 'Notes' : 'History'}
            </span>
            <div className="score-toolbar-overflow" data-testid="score-toolbar-tools">
              <span className="score-control-sub-label">{writeMode ? 'Enter' : 'Edit'}</span>
              <div
                className="score-context-tools"
                hidden={!editable || arrangeMode || (!writeMode && !selectedEvent)}
              >
                <div className="btn-group" role="group" aria-label="Move entry caret">
                  <button
                    type="button"
                    aria-label="Move caret left"
                    aria-keyshortcuts="ArrowLeft"
                    disabled={!editable}
                    title="Move the entry caret left (Left Arrow)"
                    onClick={() => stepEntryCursor(-1)}
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    aria-label="Move caret right"
                    aria-keyshortcuts="ArrowRight"
                    disabled={!editable}
                    title="Move right; add a bar when the caret reaches the end (Right Arrow)"
                    onClick={() => stepEntryCursor(1)}
                  >
                    →
                  </button>
                  <button
                    type="button"
                    aria-label="Raise pitch"
                    aria-keyshortcuts="ArrowUp"
                    disabled={!editable}
                    title="Raise the selected or pending pitch (Up Arrow)"
                    onClick={() => nudgeEntryCursor(1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    aria-label="Lower pitch"
                    aria-keyshortcuts="ArrowDown"
                    disabled={!editable}
                    title="Lower the selected or pending pitch (Down Arrow)"
                    onClick={() => nudgeEntryCursor(-1)}
                  >
                    ↓
                  </button>
                </div>
                <div
                  className="btn-group score-pitch-buttons"
                  role="group"
                  aria-label="Note pitch"
                  hidden={!writeMode}
                >
                  {(['A', 'B', 'C', 'D', 'E', 'F', 'G'] as const).map((letter) => (
                    <button
                      key={letter}
                      type="button"
                      aria-label={`Enter ${letter} note`}
                      aria-keyshortcuts={letter}
                      disabled={!editable || !writeMode}
                      title={`Enter a ${letter} note (${letter})`}
                      onClick={() => enterNoteForLetter(letter.toLowerCase())}
                    >
                      {letter}
                    </button>
                  ))}
                </div>
                <div
                  className="btn-group"
                  role="group"
                  aria-label={
                    writeMode ? 'Accidental for next note' : 'Accidental for the selected note'
                  }
                  style={{ display: 'flex', gap: '0.25rem' }}
                >
                  {ENTRY_ACCIDENTALS.map((accidental) => (
                    <button
                      key={accidental.value}
                      hidden={!writeMode && accidental.value === 'key'}
                      type="button"
                      data-testid={`entry-accidental-${accidental.value}`}
                      aria-label={
                        accidental.value === 'key'
                          ? 'Use key signature'
                          : `Use ${accidental.value} accidental`
                      }
                      aria-keyshortcuts={accidental.shortcut}
                      aria-pressed={
                        writeMode
                          ? entryAccidental === accidental.value
                          : !!selectedWrittenPitch &&
                            (accidental.value === 'natural'
                              ? selectedWrittenPitch.alter === 0
                              : accidental.value === 'sharp'
                                ? selectedWrittenPitch.alter === 1
                                : accidental.value === 'flat'
                                  ? selectedWrittenPitch.alter === -1
                                  : false)
                      }
                      disabled={
                        !editable || (!writeMode && (accidental.value === 'key' || !selectedId))
                      }
                      title={`${accidental.title} (${accidental.shortcut})`}
                      onClick={() => chooseEntryAccidental(accidental.value)}
                      style={{
                        fontWeight: writeMode && entryAccidental === accidental.value ? 600 : 400,
                      }}
                    >
                      {accidental.label}
                      <span className="score-duration-key" aria-hidden="true">
                        {accidental.shortcut}
                      </span>
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  data-testid="entry-tie"
                  aria-label="Toggle outgoing tie (T)"
                  aria-keyshortcuts="T +"
                  aria-pressed={selectedHead?.tie === 'start' || selectedHead?.tie === 'both'}
                  disabled={!editable || (!selectedId && !entryCursor)}
                  title="Toggle the outgoing tie on the selected or previous note (T or +)"
                  onClick={() => store.getState().scoreToggleOutgoingTie(writeMode)}
                >
                  Tie
                  <span className="score-duration-key" aria-hidden="true">
                    T/+
                  </span>
                </button>
                <button
                  type="button"
                  hidden={!writeMode}
                  aria-pressed={restMode}
                  aria-label={writeMode ? 'Enter rest (0)' : 'Toggle rest entry'}
                  aria-keyshortcuts="0 R"
                  disabled={!editable || (writeMode && !entryCursor)}
                  onClick={() => (writeMode ? enterRest() : setRestMode((enabled) => !enabled))}
                  title="Place rests with staff clicks; in Write mode use 0 or R for one rest"
                >
                  {writeMode ? 'Insert rest' : restMode ? 'Rest clicks: on' : 'Rest clicks'}
                </button>
                <button
                  type="button"
                  aria-label="Toggle dotted duration"
                  aria-keyshortcuts="."
                  aria-pressed={isDottedEntryDuration(displayDuration)}
                  disabled={!editable || selectedFixedDuration}
                  title="Toggle the augmentation dot (.)"
                  onClick={toggleEntryDot}
                >
                  Dot
                </button>
                <button
                  type="button"
                  hidden={!writeMode}
                  data-testid="entry-triplet"
                  aria-label="Insert triplet"
                  aria-keyshortcuts="Control+3 Meta+3"
                  disabled={!editable || !writeMode}
                  title="Insert three of the current note value in the time of two (Ctrl/⌘+3)"
                  onClick={insertTripletAtCursor}
                >
                  Triplet
                  <span className="score-duration-key" aria-hidden="true">
                    ⌘3
                  </span>
                </button>
                <button
                  type="button"
                  aria-pressed={entryVoice === 1}
                  hidden={!writeMode}
                  data-testid="voice-toggle"
                  disabled={!editable || !canEditSecondVoice}
                  onClick={() => store.getState().setEntryVoice(entryVoice === 1 ? 0 : 1)}
                  title={
                    canEditSecondVoice
                      ? 'Enter notes into the second voice (stems down)'
                      : 'Second-voice entry is currently limited to single-staff parts'
                  }
                >
                  {entryVoice === 1 ? 'Voice 2' : 'Voice 1'}
                </button>
              </div>
              {!writeMode && !arrangeMode && editable && expressionControls && (
                <Popover label="Expression" title="Note expression" testid="score-note-expression">
                  {expressionControls}
                </Popover>
              )}
              {staffControls}
              <button
                type="button"
                hidden={!writeMode}
                aria-pressed={showPiano}
                disabled={!editable}
                onClick={() => setShowPiano((visible) => !visible)}
              >
                {showPiano ? 'Hide piano' : 'Piano'}
              </button>
              {
                <>
                  <button
                    type="button"
                    onClick={() => restoreScoreEdit('undo')}
                    disabled={fullScore || !canUndo}
                  >
                    Undo
                  </button>
                  <button
                    type="button"
                    onClick={() => restoreScoreEdit('redo')}
                    disabled={fullScore || !canRedo}
                  >
                    Redo
                  </button>
                  <button
                    type="button"
                    hidden={!selectedId && passageIdSet.size === 0}
                    data-testid="score-copy"
                    onClick={copyScore}
                    disabled={fullScore || (passageIdSet.size === 0 && !selectedId)}
                  >
                    Copy
                  </button>
                  <button
                    type="button"
                    hidden={!selectedId && passageIdSet.size === 0}
                    data-testid="score-cut"
                    aria-keyshortcuts="Control+X Meta+X"
                    onClick={cutScore}
                    disabled={fullScore || !editable || (passageIdSet.size === 0 && !selectedId)}
                  >
                    Cut
                  </button>
                  <button
                    type="button"
                    hidden={!hasClipboard}
                    data-testid="score-paste"
                    aria-keyshortcuts="Control+V Meta+V"
                    onClick={() => pasteScore()}
                    disabled={fullScore || !editable || !hasClipboard}
                  >
                    Paste
                  </button>
                  {hasStructuralClipboard && editable && !fullScore && (
                    <Popover
                      label="Paste special"
                      title="Choose what to paste"
                      testid="score-paste-special"
                    >
                      {(close) => (
                        <div className="score-mobile-more-actions">
                          {(
                            [
                              ['all', 'Everything'],
                              ['notes', 'Notes only'],
                              ['harmony', 'Chords only'],
                              ['form', 'Form marks only'],
                            ] as const
                          ).map(([mode, label]) => (
                            <button
                              key={mode}
                              type="button"
                              onClick={() => {
                                pasteScore(mode);
                                close();
                              }}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      )}
                    </Popover>
                  )}
                  {clipboardNotice && (
                    <span className="score-clipboard-notice" role="status">
                      {clipboardNotice}
                    </span>
                  )}
                  <button
                    type="button"
                    hidden={!selectedId || arrangeMode}
                    onClick={() => store.getState().scoreDeleteSelected()}
                    disabled={!editable || !selectedId}
                  >
                    Delete
                  </button>
                  <div
                    className="btn-group"
                    role="group"
                    aria-label="Edit selected note"
                    hidden={!selectedId || writeMode || arrangeMode}
                    style={{ display: 'flex', gap: '0.25rem' }}
                  >
                    <button
                      type="button"
                      title="Up an octave (⇧↑)"
                      onClick={() => store.getState().scoreNudgeSelected(12)}
                      disabled={!editable || !selectedId}
                    >
                      8va
                    </button>
                    <button
                      type="button"
                      title="Down an octave (⇧↓)"
                      onClick={() => store.getState().scoreNudgeSelected(-12)}
                      disabled={!editable || !selectedId}
                    >
                      8vb
                    </button>
                    <button
                      type="button"
                      title="Respell enharmonically (J)"
                      onClick={() => store.getState().scoreRespellSelected()}
                      disabled={!editable || !selectedId}
                    >
                      Respell
                    </button>
                  </div>
                </>
              }
            </div>
          </div>

          {!compact && (
            <div className="score-file-popover">
              <Popover
                label="File…"
                title="Score file actions"
                testid="score-file-toggle"
                align="right"
              >
                {renderFileActions}
              </Popover>
            </div>
          )}

          {!compactPartOptions && partControls}

          <details className="score-view-details">
            <summary>{compact ? 'Options' : 'View'}</summary>
            <div
              className="score-control-row score-view-controls"
              role="group"
              aria-label="Score view"
              data-testid="score-view-controls"
            >
              <span className="score-control-label">View</span>
              {compactPartOptions && partControls}
              <button
                type="button"
                data-testid="lyrics-toggle"
                aria-pressed={showLyrics}
                disabled={!hasLyrics}
                onClick={() => store.getState().setShowLyrics(!showLyrics)}
                title={hasLyrics ? 'Show or hide lyrics' : 'This score has no lyrics'}
              >
                {showLyrics ? 'Lyrics: on' : 'Lyrics: off'}
              </button>
              <button
                type="button"
                data-testid="bar-numbers-toggle"
                aria-pressed={showBarNumbers}
                onClick={() => store.getState().setShowBarNumbers(!showBarNumbers)}
                title="Show or hide the bar numbers"
              >
                {showBarNumbers ? 'Bars: on' : 'Bars: off'}
              </button>
              <button
                type="button"
                data-testid="score-pages-toggle"
                aria-pressed={sheetPages}
                onClick={() => setSheetPages(!sheetPages)}
                title="Lay the score out as side-by-side pages that fit the screen — widen the window for more pages"
              >
                {sheetPages ? 'Pages: on' : 'Pages: off'}
              </button>
              <span className="muted">Size</span>
              <div className="staff-size-control" data-testid="staff-size">
                <ScaleSlider
                  value={scoreScale}
                  onChange={setScoreScale}
                  label="Score size"
                  testid="score-scale"
                  presets={false}
                />
              </div>
            </div>
          </details>
        </div>

        <div className="score-entry-guide" data-testid="score-entry-guide">
          <Popover
            title="Go to bar"
            testid="score-bar-navigation"
            label={
              <span
                className="score-entry-position"
                data-testid="score-selection-inspector"
                aria-live="polite"
              >
                {arrangeMode
                  ? `Bars & chords · ${selectedMeasureId ? `bar ${Math.max(1, score.form.findIndex((measure) => measure.id === selectedMeasureId) + 1)} selected` : 'click a bar in the score'}`
                  : chordEditorVisible && selectedChord
                    ? `${chartChordSymbol(selectedChord.chord)} · bar ${selectedChord.measureIndex + 1}`
                    : !writeMode && selectedEvent
                      ? `${selectedPitchLabel} · bar ${selectedEvent.measureIndex + 1}, beat ${Number(selectedEvent.beat.toFixed(3))} · ${entryDurationLabel(displayDuration)}${selectedFixedDuration ? (selectedSlot?.tupletGroup ? ' · tuplet duration is fixed' : ' · grace note') : ''}`
                      : fullScore
                        ? 'Full score view · choose a part to edit its notes'
                        : entryCursor && writeMode
                          ? `Bar ${Math.max(1, score.form.findIndex((measure) => measure.id === entryCursor.measureId) + 1)}, beat ${Number(entryCursor.beat.toFixed(3))} · ${entryDurationLabel(entryDuration)} entry${entryAccidental === 'key' ? '' : ` · ${entryAccidental} next`}`
                          : `Bar ${currentBarIndex + 1} · Select a note or chord`}
                <span aria-hidden="true"> ▾</span>
              </span>
            }
          >
            {(close) => (
              <div className="score-bar-jump-panel">
                {/* On a phone the bar strip lives here rather than taking a
                    row of the dock above the music. */}
                {compact && (
                  <ScoreFormOverview
                    grid
                    measures={score.form}
                    activeIndex={currentBarIndex}
                    selectedIds={passageIdSet}
                    visibleIds={visibleMeasureIds}
                    playingIndex={playingBarIndex}
                    onNavigate={(index, extend) => {
                      navigateFromOverview(index, extend);
                      close();
                    }}
                  />
                )}
                <form
                  className="score-bar-jump"
                  onSubmit={(event) => {
                    event.preventDefault();
                    const value = new FormData(event.currentTarget).get('bar');
                    jumpToBar(Number(value));
                    close();
                  }}
                >
                  <label htmlFor="score-bar-number">Go to bar</label>
                  <div>
                    <input
                      id="score-bar-number"
                      name="bar"
                      aria-label="Bar number"
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={score.form.length}
                      step={1}
                      defaultValue={currentBarIndex + 1}
                      required
                      autoFocus={!compact}
                    />
                    <span className="muted">of {score.form.length}</span>
                    <button type="submit" className="btn btn-sm" disabled={!score.form.length}>
                      Go
                    </button>
                  </div>
                </form>
              </div>
            )}
          </Popover>
          <div className="score-entry-actions">
            <button
              type="button"
              className="btn btn-sm score-audition"
              data-testid="score-audition"
              aria-label={
                firstAuditionBar === lastAuditionBar
                  ? `Play bar ${firstAuditionBar + 1}`
                  : `Play passage, bars ${firstAuditionBar + 1} to ${lastAuditionBar + 1}`
              }
              title={`Play ${auditionLabel.toLowerCase()} · ${playbackScope === 'score' || fullScore ? 'all written parts' : (activePartDefinition?.name ?? 'Lead')}`}
              disabled={!audioEnabled || !auditionRange}
              onClick={auditionBars}
            >
              <Play size={14} aria-hidden="true" />
              <span>{passageIndices.length > 1 ? 'Play passage' : 'Play bar'}</span>
            </button>
            <details className="score-shortcuts-disclosure">
              <summary title="Keyboard shortcuts">Keyboard shortcuts</summary>
              <span className="score-shortcut-help muted">
                <kbd>N</kbd> write · <kbd>A–G</kbd> note · <kbd>3–7</kbd> duration · <kbd>.</kbd>{' '}
                dot · <kbd># / − / =</kbd> accidental · <kbd>0 / R</kbd> rest · <kbd>T / +</kbd> tie
                · <kbd>arrows</kbd> move or pitch · <kbd>Shift + arrows</kbd> select bars ·{' '}
                <kbd>Space</kbd> play / pause · <kbd>Esc</kbd> select
              </span>
            </details>
          </div>
        </div>

        {!compact && (
          <ScoreFormOverview
            measures={score.form}
            activeIndex={currentBarIndex}
            selectedIds={passageIdSet}
            visibleIds={visibleMeasureIds}
            playingIndex={playingBarIndex}
            onNavigate={navigateFromOverview}
          />
        )}

        {!compact && editable && !writeMode && !arrangeMode && noteheadControls}
        {/* The piano belongs to the entry dock, not the scrolling page: below it,
            scrolling to a later bar slid the keys under the sticky toolbar. */}
        {showPiano && writeMode && (
          <div className="score-piano-dock no-print">
            <PianoInput onNote={(midi) => store.getState().scoreEnterAtCursor(midi)} />
          </div>
        )}
      </div>

      {showPaste && (
        <div className="score-paste no-print" style={{ marginBottom: '0.75rem' }}>
          <textarea
            aria-label="Paste a score"
            data-testid="paste-textarea"
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder="Paste MusicXML or ABC here…"
            style={{
              width: '100%',
              minHeight: '6rem',
              fontFamily: 'var(--font-mono, monospace)',
            }}
          />
          <button type="button" onClick={doPasteImport} disabled={!pasteText.trim()}>
            Import pasted score
          </button>
        </div>
      )}

      <div
        id="score-form-editor-panel"
        className="score-arrangement-panel no-print"
        data-active={arrangeMode ? 'true' : 'false'}
        hidden={!arrangeMode}
        style={{ marginBottom: '0.75rem' }}
      >
        <ScoreFormEditor embedded />
      </div>

      <div
        ref={setSurfaceRef}
        className="score-surface score-editor-surface"
        data-score-scale=""
        data-testid="score-editor-surface"
        role="application"
        aria-label="Score editor"
        tabIndex={0}
        onKeyDown={onScoreKeyDown}
        style={{ overflowX: 'auto', padding: '1rem 0', ...scaleVars({ score: scoreScale }) }}
      >
        {pager.paged ? (
          <div ref={pager.containerRef} className="sheet-pages">
            <SheetPageCards
              gridRef={pager.gridRef}
              columns={pager.columns}
              pageWidth={pager.pageWidth}
              minHeight={pager.minHeight}
              windows={pager.windows}
              render={(win, i) => (
                <>
                  {/* Title and composer print ON page 1, as an engraved score
                      does — later pages open with music at the top. */}
                  {i === 0 && (
                    <div className="sheet-page-header">
                      <ChartHeader chart={chart} />
                    </div>
                  )}
                  <div
                    ref={i === 0 ? metricsRef : undefined}
                    className="score-surface score-page-surface"
                    style={{ padding: '0.5rem 0' }}
                  >
                    {renderScoreView(win, i === 0)}
                  </div>
                </>
              )}
            />
          </div>
        ) : (
          <>
            {/* Printed-score masthead: the chart projection mirrors the score's
                title/composer, so imported MusicXML metadata shows here too. The
                tempo chip stays off — tempoText ("Sehr langsam") prints in the
                score itself. */}
            <div style={{ padding: '0 0.75rem' }}>
              <ChartHeader chart={chart} />
            </div>
            {renderScoreView()}
          </>
        )}
        {/* Paper: one block per engraved system, the masthead opening page 1
            (the studio prints the score it is editing, chrome and all gone). */}
        {printing && (
          <PrintSheet
            header={<ChartHeader chart={chart} />}
            count={pager.systemCount}
            render={(systemWindow) => (
              <div className="score-surface print-score-surface">
                {renderScoreView(systemWindow, false)}
              </div>
            )}
          />
        )}
      </div>

      {chordEditorVisible &&
        selectedChord &&
        (reharmPreview ? (
          <div
            className="score-chord-editor score-chord-preview no-print"
            role="group"
            aria-label="Harmony preview"
          >
            <p>This chord is part of a harmony preview. Keep the preview to edit it.</p>
            <div className="row">
              <button
                className="btn btn-sm btn-primary"
                type="button"
                onClick={() => {
                  store.getState().keepReharmPreview();
                  setBarJumpRequest((request) => request + 1);
                }}
              >
                Keep and edit
              </button>
              <button
                className="btn btn-sm"
                type="button"
                onClick={() => {
                  store.getState().cancelReharmPreview();
                  finishEntry();
                  focusScore();
                }}
              >
                Discard preview
              </button>
              <button
                className="btn btn-sm"
                type="button"
                onClick={() => {
                  finishEntry();
                  focusScore();
                }}
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <ScoreChordEditor
            key={`${score.id}:${selectedChord.chord.id}`}
            chord={selectedChord.chord}
            barNumber={selectedChord.measureIndex + 1}
            musicKey={chart.key}
            canUndo={canUndo}
            canRedo={canRedo}
            onUpdate={(patch) => {
              const current = store.getState();
              if (current.reharmPreview || !findChord(current.score, selectedChord.chord.id))
                return;
              current.updateChord(selectedChord.chord.id, patch);
              setBarJumpRequest((request) => request + 1);
            }}
            onUndo={() => restoreScoreEdit('undo')}
            onRedo={() => restoreScoreEdit('redo')}
            onClose={() => {
              finishEntry();
              focusScore();
            }}
          />
        ))}
      {compactNoteEditor && selectedEvent && (
        <ScoreNoteEditor
          pitchLabel={selectedPitchLabel}
          rest={selectedEvent.rest}
          duration={displayDuration}
          durationLabel={entryDurationLabel(displayDuration)}
          durations={DURATIONS}
          fixedDuration={selectedFixedDuration}
          dotted={isDottedEntryDuration(displayDuration)}
          tied={selectedHead?.tie === 'start' || selectedHead?.tie === 'both'}
          canUndo={canUndo}
          canRedo={canRedo}
          canPaste={hasClipboard}
          previousId={adjacentNotes.previousId}
          nextId={adjacentNotes.nextId}
          onNavigate={selectAdjacentNote}
          onPitch={(semitones) => store.getState().scoreNudgeSelected(semitones)}
          onDuration={chooseEntryDuration}
          onDot={toggleEntryDot}
          onTie={toggleOutgoingTie}
          onAccidental={chooseEntryAccidental}
          onRespell={() => store.getState().scoreRespellSelected()}
          onUndo={() => restoreScoreEdit('undo')}
          onRedo={() => restoreScoreEdit('redo')}
          onCopy={copyScore}
          onCut={cutScore}
          onPaste={() => pasteScore()}
          onDelete={deleteMobileNote}
          onClose={() => {
            finishEntry();
            focusScore();
          }}
          staffControls={staffControls}
          expressionControls={expressionControls}
          noteheadControls={noteheadControls}
        />
      )}
      {compactDeleteUndo && (
        <div
          className="score-mobile-note-editor score-mobile-delete-undo no-print"
          role="group"
          aria-label="Note deleted"
          data-testid="score-mobile-note-editor"
        >
          <span role="status">Note deleted</span>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => restoreScoreEdit('undo')}
            disabled={!canUndo}
          >
            Undo
          </button>
          <button
            type="button"
            className="btn btn-sm"
            aria-label="Finish editing note"
            onClick={() => {
              finishEntry();
              focusScore();
            }}
          >
            Done
          </button>
        </div>
      )}
      {/* The studio edits the concert-pitch truth, so it does not read in an
          edition; the Transposition sheet says so instead of hiding the row. */}
      <BandTransport
        band={band}
        onPlay={playBandFromCaret}
        readingEditions={false}
        editScoreTempo
        alwaysVisible
        playback={
          playbackScope === 'band'
            ? undefined
            : {
                transport: player.transport,
                onPlay: playWrittenFromCaret,
                onPause: player.pause,
                onResume: player.resume,
                onStop: player.stop,
              }
        }
        playbackSelector={
          <>
            <span className="mixer-toggle" aria-hidden="true">
              Play
            </span>
            <select
              aria-label="Playback source"
              value={playbackScope}
              onChange={(event) =>
                choosePlaybackScope(event.target.value as 'band' | 'score' | 'part')
              }
            >
              <option value="band">Full band</option>
              <option value="score">Written score</option>
              <option value="part" disabled={fullScore}>
                Selected part{fullScore ? '' : ` · ${activePartDefinition?.name ?? 'Lead'}`}
              </option>
            </select>
          </>
        }
      />
    </section>
  );
}
